import { useEffect, useState, useMemo, useRef } from 'react'
import { loadLeaflet } from '../lib/leaflet'
import { OSM_ATTRIBUTION, OSM_TEXT } from '../lib/legal'
import { fetchRows } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { CLIENT_COLS } from '../lib/clientCols'
import { deleteWithUndo } from '../lib/undoDelete'
import DateField from '../components/DateField'
import html2pdf from 'html2pdf.js'
import { generateWatchBill, verifyWatchBill } from '../lib/watchBill'
import { filePath } from '../lib/filePaths'

// '12:30', '12:30:00' or '12:30 PM' -> '12:30' (00:00 if empty/unreadable)
function toHHMM(t) {
  const m = String(t || '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i)
  if (!m) return '00:00'
  let h = parseInt(m[1], 10)
  if (m[3]) { const pm = /PM/i.test(m[3]); if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12 }
  return String(h).padStart(2, '0') + ':' + m[2]
}

// ============================================================
// HELPERS
// ============================================================
const getVal = v => (v && typeof v === 'object' && 'value' in v) ? (v.value ?? '') : (v ?? '')

function localToUtc(dateStr, timeStr) {
  // dateStr 'YYYY-MM-DD', timeStr 'HH:MM' (assumed local) -> UTC 'HH:MM'
  if (!dateStr || !timeStr) return ''
  try {
    const d = new Date(dateStr + 'T' + timeStr)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  } catch { return '' }
}

// signature pad: attaches mouse + touch drawing to a canvas by id (HoR pattern)
function sigCanvas(id) {
  return el => {
    if (!el) return
    el.width = el.offsetWidth || 260
    el.height = 90
    const ctx = el.getContext('2d')
    ctx.strokeStyle = '#e8e8e8'; ctx.lineWidth = 1.6; ctx.lineCap = 'round'
    let drawing = false
    el.onmousedown = e => { drawing = true; ctx.beginPath(); ctx.moveTo(e.offsetX, e.offsetY) }
    el.onmousemove = e => { if (!drawing) return; ctx.lineTo(e.offsetX, e.offsetY); ctx.stroke() }
    el.onmouseup = () => { drawing = false }
    el.onmouseleave = () => { drawing = false }
    el.ontouchstart = e => { e.preventDefault(); drawing = true; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.beginPath(); ctx.moveTo(t.clientX-r.left, t.clientY-r.top) }
    el.ontouchmove = e => { e.preventDefault(); if (!drawing) return; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.lineTo(t.clientX-r.left, t.clientY-r.top); ctx.stroke() }
    el.ontouchend = () => { drawing = false }
  }
}
function clearCanvas(id) {
  const c = document.getElementById(id); if (c) c.getContext('2d').clearRect(0,0,c.width,c.height)
}
function todayISO() { return new Date().toISOString().split('T')[0] }
function fmtHrs(h) { const H = Math.floor(h); const M = Math.round((h - H) * 60); return H + 'h ' + (M < 10 ? '0' : '') + M + 'm' }

// haversine distance between two lat/lon points, in nautical miles
function nmBetween(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return 0
  const toRad = d => d * Math.PI / 180
  const R = 3440.065 // earth radius in NM
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon)
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat)
  const h = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLon/2)**2
  return 2 * R * Math.asin(Math.sqrt(h))
}
// cumulative leg distances along an ordered waypoint list -> {legs:[nm], total, cum:[nm to each point]}
function distToSegmentNm(p, a, b) {
  // approximate: project in equirectangular space scaled by cos(lat), then nmBetween
  const latRef = (a.lat + b.lat) / 2 * Math.PI / 180
  const toXY = (q) => ({ x: q.lon * Math.cos(latRef), y: q.lat })
  const P = toXY(p), A = toXY(a), B = toXY(b)
  const dx = B.x - A.x, dy = B.y - A.y
  const len2 = dx*dx + dy*dy || 1e-12
  let t = ((P.x - A.x) * dx + (P.y - A.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  const proj = { lat: A.y + t * dy, lon: (A.x + t * dx) / Math.cos(latRef) }
  return nmBetween(p, proj)
}

function routeDistances(wps) {
  const legs = [], cum = [0]
  let total = 0
  for (let i = 1; i < wps.length; i++) {
    const d = nmBetween(wps[i-1], wps[i])
    legs.push(d); total += d; cum.push(total)
  }
  return { legs, total, cum }
}

// parse a GPX or RTZ route file into [{lat, lon, name}] waypoints
function parseRoute(text) {
  const doc = new DOMParser().parseFromString(text, 'application/xml')
  if (doc.querySelector('parsererror')) throw new Error('File is not valid XML.')
  const out = []
  // RTZ: <waypoint ...><position lat=".." lon=".."/></waypoint>
  const rtzWps = Array.from(doc.getElementsByTagName('waypoint'))
  if (rtzWps.length) {
    rtzWps.forEach(w => {
      const pos = w.getElementsByTagName('position')[0]
      const lat = parseFloat(pos?.getAttribute('lat'))
      const lon = parseFloat(pos?.getAttribute('lon'))
      const name = w.getAttribute('name') || ''
      if (!isNaN(lat) && !isNaN(lon)) out.push({ lat, lon, name })
    })
    if (out.length >= 2) return out
  }
  // GPX: prefer route points <rtept>, else track points <trkpt>
  let gpx = Array.from(doc.getElementsByTagName('rtept'))
  if (!gpx.length) gpx = Array.from(doc.getElementsByTagName('trkpt'))
  gpx.forEach(pt => {
    const lat = parseFloat(pt.getAttribute('lat'))
    const lon = parseFloat(pt.getAttribute('lon'))
    const nameEl = pt.getElementsByTagName('name')[0]
    const name = nameEl ? nameEl.textContent.trim() : ''
    if (!isNaN(lat) && !isNaN(lon)) out.push({ lat, lon, name })
  })
  return out
}

// weather tone helpers (colour-code wind/sea like the Weather page)
function tlWindCol(kn) { return wxWindCol(kn) }
function wxWindCol(kn) {
  if (kn == null) return 'var(--muted)'
  if (kn >= 33) return '#b4313c'    // gale+
  if (kn >= 22) return '#d4a05a'    // strong
  if (kn >= 17) return '#8a5c0a'    // fresh
  return 'var(--text)'
}
function wxWaveCol(m) {
  if (m == null) return 'var(--muted)'
  if (m >= 4) return '#b4313c'
  if (m >= 2.5) return '#d4a05a'
  if (m >= 1.25) return '#8a5c0a'
  return 'var(--text)'
}
function wxWindTone(kn) {
  const col = wxWindCol(kn)
  return col === 'var(--text)' ? { background: 'rgba(106,170,212,.12)', border: '0.5px solid #2a6a96', color: '#2a6a96' } : { background: 'rgba(0,0,0,.15)', border: '0.5px solid ' + col, color: col }
}
function wxWaveTone(m) {
  const col = wxWaveCol(m)
  return col === 'var(--text)' ? { background: 'rgba(106,170,212,.12)', border: '0.5px solid #2a6a96', color: '#2a6a96' } : { background: 'rgba(0,0,0,.15)', border: '0.5px solid ' + col, color: col }
}

// ============================================================
// SECTION DEFINITIONS — the human-attestation checklists.
// (Auto-intelligence sections are rendered separately.)
// ============================================================
const CHARTS_ITEMS = [
  ['charts_corrected', 'Charts (paper & electronic) and publications corrected and up to date'],
  ['ntm', 'Latest Notices to Mariners (NTM) available'],
  ['route_marked', 'Route marked, waypoints identified, courses & reciprocals drawn'],
  ['safe_havens', 'Safe havens / ports of refuge identified'],
  ['no_go', 'Safe distances marked and no-go areas identified'],
  ['transits_pi', 'Transits, clearing bearings and parallel index lines marked'],
  ['gps_wpt', 'GPS programmed with waypoints'],
  ['ens_wpt', 'Electronic Navigation System programmed with same waypoints'],
]
const TIDAL_ITEMS = [
  ['significant_range', 'Sailing through areas of significant tidal range'],
  ['streams_windows', 'Tidal streams and tidal windows calculated'],
  ['ukc_established', 'Minimum under-keel clearance (UKC) established'],
]
const WEATHER_ITEMS = [
  ['navarea', 'Latest NavArea warnings received and dangers noted'],
  ['forecast', 'Latest local and long-range forecasts obtained'],
  ['msi', 'Marine Safety Information (MSI) reviewed (restrictions, mammal strike, anchoring)'],
]
const ROUTING_ITEMS = [
  ['tss', 'TSS or Mandatory Reporting Schemes noted'],
  ['vts', 'Vessel Traffic Services noted'],
  ['pilot', 'Pilot requirements identified'],
  ['authorities', 'Coastguard / Customs / Immigration / Port State requirements identified'],
]
const BRIDGE_ITEMS = [
  ['radar', 'Radar sets tuned and functioning correctly'],
  ['pi_radar', 'Parallel index lines programmed to radar set(s)'],
  ['ecdis_xtd', 'ECDIS XTD corridors set per leg (if applicable)'],
  ['gmdss', 'GMDSS and emergency equipment functioning correctly'],
  ['flags', 'Courtesy and code flags available'],
  ['predeparture', 'Deck & Engineering pre-departure checks complete & logged'],
]

const SIGN_ROLES = ['Captain', 'Chief Officer', 'Third Watchleader', 'Fourth Watchleader']

// ============================================================
// MAIN COMPONENT
// ============================================================
// Tell the trip which passage plan belongs to it (server checks access).
async function linkTripToPlan(tripId, planId) {
  if (!tripId || !planId) return
  try { await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'trip_save', trip: { id: tripId, passage_plan_id: planId } }) }) } catch (e) {}
}

export default function PassagePlan() {
  const { user, config: authConfig } = useAuth()
  const [config, setConfig] = useState(null)
  const [crew, setCrew] = useState([])
  const [vp, setVp] = useState({})  // vessel particulars (Max Draft etc.)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [plans, setPlans] = useState([])
  const [view, setView] = useState('list')   // 'list' | 'edit'

  // ---- form state ----
  const [form, setForm] = useState(blankForm())

  function blankForm() {
    return {
      title: '', departure_port: '', departure_date: '', departure_time_local: '',
      arrival_port: '', arrival_date: '', arrival_time_local: '',
      watch_leaders: [],
      charts_publications: {}, tidal_info: {}, weather_msi: {},
      routing_reporting: {}, port_contacts: {}, bridge_equipment: {},
      additional_notes: '', auto_data: {}, signatures: [],
    }
  }

  // ============================================================
  // LOAD vessel config + crew + existing plans
  // ============================================================
  useEffect(() => {
    (async () => {
      try {
        // vessel config
        // the logged-in person's vessel (owner, crew login or fleet drill-in); owner lookup as fallback
        let cfg = authConfig && authConfig.id ? authConfig : null
        if (!cfg) { const { data } = await supabase.from('clients').select(CLIENT_COLS).eq('user_id', user?.id).maybeSingle(); cfg = data }
        setConfig(cfg)
        const cid = cfg?.id
        // crew (Supabase) for watch-leader auto-pull
        try {
          const c = await fetchRows('crew')
          setCrew((c || []).map(r => (getVal(r['First Name']) + ' ' + getVal(r['Last Name'])).trim()).filter(Boolean))
        } catch { /* crew optional */ }
        // vessel particulars (for auto draft) - Supabase, one row per vessel (the old Baserow table had a "Vessels Name" column)
        try {
          const vpr = await fetchRows('vessel_particulars')
          const row = (vpr && vpr[0]) ? vpr[0] : {}
          const flat = {}
          Object.keys(row).forEach(k => { flat[k] = getVal(row[k]) })
          setVp(flat)
        } catch { /* particulars optional */ }
        // existing plans
        await loadPlans(cid)
        // opened from a Trip: prefilled new plan, or that trip's existing plan
        try {
          const pre = JSON.parse(localStorage.getItem('hc_pp_prefill') || 'null')
          localStorage.removeItem('hc_pp_prefill')
          if (pre && pre.plan_id) {
            const { data: p } = await supabase.from('passage_plans').select('*').eq('id', pre.plan_id).single()
            if (p) editPlan(p)
          } else if (pre) {
            { const bf = blankForm(); const { speed_kn, ...rest } = pre; setForm({ ...bf, ...rest, auto_data: { ...(bf.auto_data || {}), ...(speed_kn ? { speed_kn: String(speed_kn) } : {}) } }) }
            setView('edit')
          }
        } catch (e) {}
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  async function loadPlans(cid) {
    const useId = cid || config?.id
    if (!useId) { setPlans([]); return }
    const { data } = await supabase.from('passage_plans').select('*').eq('client_id', useId).order('created_at', { ascending: false })
    setPlans(data || [])
  }

  // ============================================================
  // AUTO-COMPUTED values
  // ============================================================
  const depUtc = useMemo(() => localToUtc(form.departure_date, form.departure_time_local), [form.departure_date, form.departure_time_local])
  const arrUtc = useMemo(() => localToUtc(form.arrival_date, form.arrival_time_local), [form.arrival_date, form.arrival_time_local])

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function setSection(section, key, val) {
    setForm(f => ({ ...f, [section]: { ...f[section], [key]: val } }))
  }
  function toggleWatchLeader(name) {
    setForm(f => ({
      ...f,
      watch_leaders: f.watch_leaders.includes(name)
        ? f.watch_leaders.filter(n => n !== name)
        : [...f.watch_leaders, name],
    }))
  }

  // ============================================================
  // SAVE
  // ============================================================
  // calendar event on every save; running log entry once the plan is signed. Ids kept in auto_data.links
  async function syncPassageLinks(withRunningLog) {
    const links = { ...((form.auto_data || {}).links || {}) }
    if (!config?.id || !form.departure_date) return links
    const dep = toHHMM(form.departure_time_local), arr = form.arrival_time_local ? toHHMM(form.arrival_time_local) : ''
    const route = (form.departure_port || '?') + ' \u2192 ' + (form.arrival_port || '?')
    const facts = ['Departure ' + dep, arr && ('arrival ' + arr), dist.total ? dist.total.toFixed(1) + ' NM' : '', speedKn ? speedKn + ' kn' : ''].filter(Boolean).join(' \u00b7 ')
    try {
      const ev = { client_id: config.id, title: 'Passage: ' + route, type: 'Custom', start_date: form.departure_date, end_date: form.arrival_date || form.departure_date, start_time: dep, notes: facts + ' (from the passage plan)' }
      if (links.calendar_event_id) { const { data } = await supabase.from('calendar_events').update(ev).eq('id', links.calendar_event_id).select('id'); if (!data || !data.length) links.calendar_event_id = null }
      if (!links.calendar_event_id) { const { data, error } = await supabase.from('calendar_events').insert(ev).select('id').single(); if (error) console.warn('calendar', error.message); if (data) links.calendar_event_id = data.id }
    } catch (e) { console.warn('calendar link', e) }
    if (withRunningLog) {
      try {
        const fd = (form.auto_data || {}).fuel || {}
        const rl = { client_id: config.id, date: form.departure_date, departure_time: dep, voyage_origin: form.departure_port || null, voyage_destination: form.arrival_port || null,
          departure_fuel: fd.on_board ? String(fd.on_board) + ' ' + (fd.unit || 'L') : null, notes: 'Planned arrival ' + (arr || '\u2013') + ' \u00b7 ' + facts + ' (passage plan)' }
        let id = links.running_log_id
        if (!id) {
          const { data: ex } = await supabase.from('running_log').select('id').eq('client_id', config.id).eq('date', form.departure_date).ilike('voyage_origin', form.departure_port || '').limit(1)
          if (ex && ex[0]) id = ex[0].id
        }
        if (id) {
          const { data } = await supabase.from('running_log').update({ date: rl.date, departure_time: rl.departure_time, voyage_origin: rl.voyage_origin, voyage_destination: rl.voyage_destination, ...(rl.departure_fuel ? { departure_fuel: rl.departure_fuel } : {}) }).eq('id', id).select('id')
          links.running_log_id = data && data.length ? id : null
        }
        if (!links.running_log_id) { const { data, error } = await supabase.from('running_log').insert(rl).select('id').single(); if (error) console.warn('running log', error.message); if (data) links.running_log_id = data.id }
      } catch (e) { console.warn('running log link', e) }
    }
    return links
  }

  async function savePlan() {
    setSaving(true)
    try {
      const links = await syncPassageLinks(false)
      const payload = {
        client_id: config?.id,
        vessel_name: config?.vessel_name || '',
        title: form.title || (form.departure_port + ' → ' + form.arrival_port),
        departure_port: form.departure_port, departure_date: form.departure_date || null,
        departure_time_local: form.departure_time_local, departure_time_utc: depUtc,
        arrival_port: form.arrival_port, arrival_date: form.arrival_date || null,
        arrival_time_local: form.arrival_time_local, arrival_time_utc: arrUtc,
        watch_leaders: form.watch_leaders,
        charts_publications: form.charts_publications, tidal_info: form.tidal_info,
        weather_msi: form.weather_msi, routing_reporting: form.routing_reporting,
        port_contacts: form.port_contacts, bridge_equipment: form.bridge_equipment,
        additional_notes: form.additional_notes, auto_data: { ...(form.auto_data || {}), links },
        signatures: form.signatures, status: 'draft', created_by: user?.id,
      }
      if (form.id) {
        const { data: upd, error: ue } = await supabase.from('passage_plans').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', form.id).select('id')
        if (ue) { alert('Could not save the plan: ' + ue.message); return }
        if (!upd || !upd.length) { alert('\u26a0\ufe0f The plan was not saved (no permission to change this plan, or it no longer exists).'); return }
      } else {
        const { data: ins, error: ie } = await supabase.from('passage_plans').insert(payload).select('id').single()
        if (ie) { alert('Could not save the plan: ' + ie.message); return }
        if (form.trip_id && ins?.id) await linkTripToPlan(form.trip_id, ins.id)
      }
      await loadPlans()
      setView('list')
      setForm(blankForm())
    } finally { setSaving(false) }
  }

  function editPlan(p) {
    setForm({
      id: p.id, title: p.title || '',
      departure_port: p.departure_port || '', departure_date: p.departure_date || '', departure_time_local: p.departure_time_local || '',
      arrival_port: p.arrival_port || '', arrival_date: p.arrival_date || '', arrival_time_local: p.arrival_time_local || '',
      watch_leaders: p.watch_leaders || [],
      charts_publications: p.charts_publications || {}, tidal_info: p.tidal_info || {},
      weather_msi: p.weather_msi || {}, routing_reporting: p.routing_reporting || {},
      port_contacts: p.port_contacts || {}, bridge_equipment: p.bridge_equipment || {},
      additional_notes: p.additional_notes || '', auto_data: p.auto_data || {}, signatures: p.signatures || [],
    })
    setView('edit')
  }

  async function deletePlan(id) {
    const pl = plans.find(x => x.id === id), label = 'passage plan' + (pl && (pl.title || pl.name) ? ' "' + (pl.title || pl.name) + '"' : '')
    if (!window.confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'passage_plans', id, label, onUndone: () => loadPlans() }) } catch (e) { alert('Could not delete: ' + (e.message || e)) }
    await loadPlans()
  }

  // ============================================================
  // TIDAL + UKC AUTO-INTELLIGENCE
  // ============================================================
  const [tideLoading, setTideLoading] = useState(false)
  const [tideErr, setTideErr] = useState('')

  async function fetchTidesForPort(portName) {
    if (!portName) return null
    // 1. geocode the port via /api/weather
    const w = await fetch('/api/weather?port=' + encodeURIComponent(portName))
    const wd = await w.json()
    if (!w.ok || wd.latitude == null) throw new Error('Could not locate ' + portName)
    // 2. tides for that lat/lon
    const t = await fetch('/api/tides?lat=' + wd.latitude + '&lon=' + wd.longitude)
    const td = await t.json()
    return {
      port: portName,
      lat: wd.latitude, lon: wd.longitude,
      available: !!td.available,
      station: td.station || '',
      tidalRangeM: td.tidalRangeM != null ? Math.round(td.tidalRangeM * 100) / 100 : null,
      extremes: (td.extremes || []).slice(0, 8).map(e => ({
        type: e.type, heightM: e.heightM,
        time: new Date(e.time).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' }),
      })),
      lowestM: (td.extremes || []).reduce((m, e) => (e.heightM != null && e.heightM < m ? e.heightM : m), Infinity),
    }
  }

  async function fetchTidesUKC() {
    setTideLoading(true); setTideErr('')
    try {
      const dep = form.departure_port ? await fetchTidesForPort(form.departure_port) : null
      const arr = form.arrival_port ? await fetchTidesForPort(form.arrival_port) : null
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, tides: { departure: dep, arrival: arr, fetchedAt: new Date().toISOString() } } }))
    } catch (e) {
      setTideErr(e?.message || 'Tide lookup failed')
    } finally { setTideLoading(false) }
  }

  // UKC = charted depth + tide height (lowest) - draft. Stored in auto_data.ukc.
  function setUkcField(k, v) {
    setForm(f => ({ ...f, auto_data: { ...f.auto_data, ukc: { ...(f.auto_data.ukc || {}), [k]: v } } }))
  }
  const ukc = form.auto_data?.ukc || {}
  const autoDraft = parseFloat(String(vp['Max Draft'] || '').replace(/[^0-9.]/g, ''))
  const ukcResult = useMemo(() => {
    const draft = !isNaN(parseFloat(ukc.draft)) ? parseFloat(ukc.draft) : autoDraft
    const depth = parseFloat(ukc.charted_depth)
    const tideLow = form.auto_data?.tides?.departure?.lowestM
    const tideAdd = (tideLow != null && isFinite(tideLow)) ? tideLow : (parseFloat(ukc.tide_height) || 0)
    if (isNaN(draft) || isNaN(depth)) return null
    return Math.round((depth + tideAdd - draft) * 100) / 100
  }, [ukc.draft, ukc.charted_depth, ukc.tide_height, form.auto_data?.tides, autoDraft])

  // ============================================================
  // WEATHER AUTO-INTELLIGENCE
  // ============================================================
  const [wxLoading, setWxLoading] = useState(false)
  const [wxErr, setWxErr] = useState('')
  // ---- weather timeline (scrub forecast along the route) ----
  const [timeIdx, setTimeIdx] = useState(0)
  const [playing, setPlaying] = useState(false)
  // unified list of timestamps from the departure point's hourly (all share the same grid)
  const timeline = useMemo(() => {
    const wx = form.auto_data?.weather
    const src = wx?.departure?.hourly || wx?.waypoints?.[0]?.hourly || wx?.arrival?.hourly || []
    let times = src.map(h => h.time)
    // constrain to the passage window (departure -6h .. arrival +6h) if dates are set
    if (form.departure_date) {
      const depRaw = new Date(form.departure_date + 'T' + (form.departure_time_local || '00:00')).getTime()
      const dep = depRaw - 6*3600*1000
      // passage-end window: later of typed arrival OR real passage time (speed x distance)
      const spd = parseFloat(form.auto_data?.speed_kn) || null
      const rw = form.auto_data?.route_waypoints
      let passEnd = depRaw + 24*3600*1000
      if (spd && rw && rw.length >= 2) {
        const rd = routeDistances(rw)
        if (rd.total) passEnd = depRaw + (rd.total / spd) * 3600*1000
      }
      const typedArr = form.arrival_date ? new Date(form.arrival_date + 'T' + (form.arrival_time_local || '00:00')).getTime() : 0
      const arr = Math.max(passEnd, typedArr) + 6*3600*1000
      const win = times.filter(t => { const ms = new Date(t).getTime(); return ms >= dep && ms <= arr })
      if (win.length >= 2) times = win
    }
    return times
  }, [form.auto_data?.weather, form.departure_date, form.departure_time_local, form.arrival_date, form.arrival_time_local, form.auto_data?.speed_kn, form.auto_data?.route_waypoints])

  // timeline opens at the departure time (nearest forecast slot), and follows departure date/time changes
  useEffect(() => {
    if (!timeline.length) return
    const dep = form.departure_date ? new Date(form.departure_date + 'T' + (form.departure_time_local || '00:00')).getTime() : NaN
    if (isNaN(dep)) { if (timeIdx >= timeline.length) setTimeIdx(0); return }
    let best = 0, bd = Infinity
    timeline.forEach((t, i) => { const d = Math.abs(new Date(t).getTime() - dep); if (d < bd) { bd = d; best = i } })
    setTimeIdx(best)
  }, [timeline.length, form.departure_date, form.departure_time_local]) // eslint-disable-line
  const [tlFrac, setTlFrac] = useState(0) // 0..1 between timeIdx and timeIdx+1 (smooth boat)
  useEffect(() => {
    if (!playing || timeline.length === 0) return
    const STEP_MS = 1800, TICK = 60 // 1.8s per forecast step, 60ms frames
    const id = setInterval(() => {
      setTlFrac(f => {
        const nf = f + TICK / STEP_MS
        if (nf >= 1) { setTimeIdx(i => (i + 1) % timeline.length); return 0 }
        return nf
      })
    }, TICK)
    return () => clearInterval(id)
  }, [playing, timeline.length])
  // reset fraction whenever user scrubs manually or playback stops
  useEffect(() => { if (!playing) setTlFrac(0) }, [playing])

  // weather at a given point for the selected timeline index (falls back to current)
  function wxAtTime(point) {
    if (!point) return {}
    const t = timeline[timeIdx]
    if (t && point.hourly) {
      const h = point.hourly.find(x => x.time === t)
      if (h) return h
    }
    return point.current || {}
  }

  // ---- custom route waypoints (editable map) ----
  // built from auto_data.route_waypoints if present, else from the weather route endpoints
  const routeWaypoints = useMemo(() => {
    const saved = form.auto_data?.route_waypoints
    if (saved && saved.length >= 2) return saved
    // seed from the full fetched weather sequence (departure + waypoints + arrival)
    const wx = form.auto_data?.weather
    if (wx && (wx.departure || wx.arrival)) {
      const seq = [wx.departure, ...(wx.waypoints || []), wx.arrival].filter(Boolean)
      const pts = seq.filter(p => p.lat != null && p.lon != null).map((p, i, arr) => ({
        lat: p.lat, lon: p.lon, name: p.location || p.label, locked: (i === 0 || i === arr.length - 1),
      }))
      if (pts.length >= 2) return pts
    }
    const r = wx?.route
    if (r) return [
      { lat: r.lat1, lon: r.lon1, name: form.departure_port || 'Departure', locked: true },
      { lat: r.lat2, lon: r.lon2, name: form.arrival_port || 'Arrival', locked: true },
    ]
    return []
  }, [form.auto_data?.route_waypoints, form.auto_data?.weather, form.departure_port, form.arrival_port])

  // ---- speed / distance / ETA ----
  const speedKn = parseFloat(form.auto_data?.speed_kn) || null
  // boat position at scrubbed time -> weather there (nearest waypoint), for the "You are here" row
  function boatHere() {
    const wx = form.auto_data?.weather
    if (!wx) return null
    const t = timeline[timeIdx]
    const dep = form.departure_date ? new Date(form.departure_date + 'T' + (form.departure_time_local || '00:00')).getTime() : null
    if (!speedKn || !dep || !t) return null
    const wps = routeWaypoints
    if (wps.length < 2) return null
    const hrs = (new Date(t).getTime() - dep) / 3600000
    if (hrs <= 0) return null
    const rd = routeDistances(wps)
    const nm = Math.min(speedKn * hrs, rd.total)
    let leg = 1
    while (leg < rd.cum.length && rd.cum[leg] < nm) leg++
    const a = wps[leg-1], b = wps[leg]
    const segStart = rd.cum[leg-1], segLen = rd.legs[leg-1] || 1e-9
    const f = (nm - segStart) / segLen
    const lat = a.lat + (b.lat - a.lat) * f, lon = a.lon + (b.lon - a.lon) * f
    // nearest forecast point to the boat
    const seq = [wx.departure, ...(wx.waypoints || []), wx.arrival].filter(Boolean)
    let near = null, best = Infinity
    seq.forEach(pt => { if (pt.lat == null) return; const d = Math.hypot(pt.lat - lat, pt.lon - lon); if (d < best) { best = d; near = pt } })
    if (!near) return null
    return wxAtTime(near)
  }
  const dist = useMemo(() => routeDistances(routeWaypoints), [routeWaypoints])
  // passage time (hours) from total distance / speed
  const passageHours = (speedKn && dist.total) ? dist.total / speedKn : null
  // arrival calculated from speed: departure + distance / speed (rounded to 5 min)
  useEffect(() => {
    if (!passageHours || !form.departure_date) return
    const dep = new Date(form.departure_date + 'T' + toHHMM(form.departure_time_local))
    if (isNaN(dep.getTime())) return
    const a = new Date(Math.round((dep.getTime() + passageHours * 3600e3) / 300e3) * 300e3)
    const pad = n => String(n).padStart(2, '0')
    const ad = a.getFullYear() + '-' + pad(a.getMonth() + 1) + '-' + pad(a.getDate())
    const at = pad(a.getHours()) + ':' + pad(a.getMinutes())
    if (form.arrival_date !== ad || toHHMM(form.arrival_time_local) !== at) setForm(f => ({ ...f, arrival_date: ad, arrival_time_local: at }))
  }, [passageHours, form.departure_date, form.departure_time_local]) // eslint-disable-line
  // required speed if arrival time is set instead
  const requiredSpeed = useMemo(() => {
    if (!form.departure_date || !form.arrival_date || !dist.total) return null
    const dep = new Date(form.departure_date + 'T' + (form.departure_time_local || '00:00')).getTime()
    const arr = new Date(form.arrival_date + 'T' + (form.arrival_time_local || '00:00')).getTime()
    const hrs = (arr - dep) / 3600000
    return hrs > 0 ? Math.round((dist.total / hrs) * 10) / 10 : null
  }, [form.departure_date, form.arrival_date, form.departure_time_local, form.arrival_time_local, dist.total])
  const [importMsg, setImportMsg] = useState('')
  const [startBusy, setStartBusy] = useState(false)
  async function onStartFromPorts() {
    if (!form.departure_port || !form.arrival_port) { setImportMsg('Enter both departure and arrival ports first.'); return }
    setStartBusy(true); setImportMsg('')
    try {
      const dep = await fetchWeatherByPort(form.departure_port)
      const arr = await fetchWeatherByPort(form.arrival_port)
      const wps = [
        { lat: dep.latitude, lon: dep.longitude, name: form.departure_port, locked: true },
        { lat: arr.latitude, lon: arr.longitude, name: form.arrival_port, locked: true },
      ]
      setRouteWaypoints(wps)
      setImportMsg('Route started. Click the map to add waypoints, then Fetch weather.')
    } catch (e) {
      setImportMsg('Could not locate ports: ' + (e.message || 'geocode failed'))
    } finally { setStartBusy(false) }
  }
  function onImportRoute(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // allow re-importing the same file
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const pts = parseRoute(String(reader.result))
        if (pts.length < 2) { setImportMsg('Could not read 2+ waypoints from that file.'); return }
        const wps = pts.map((p, i, arr) => ({ lat: p.lat, lon: p.lon, name: p.name || (i === 0 ? 'Departure' : i === arr.length - 1 ? 'Arrival' : 'WP' + i), locked: (i === 0 || i === arr.length - 1) }))
        setRouteWaypoints(wps)
        // clear stale weather so it re-fetches along the imported route
        setForm(f => ({ ...f, auto_data: { ...f.auto_data, weather: undefined } }))
        setImportMsg('Imported ' + wps.length + ' waypoints. Now Fetch weather to see the route on the map and along-route forecast.')
      } catch (err) {
        setImportMsg('Import failed: ' + (err.message || 'could not parse file'))
      }
    }
    reader.onerror = () => setImportMsg('Could not read the file.')
    reader.readAsText(file)
  }
  const [depthBusy, setDepthBusy] = useState(false)
  const [depthMsg, setDepthMsg] = useState('')
  const [genBusy, setGenBusy] = useState(false)
  const [genMsg, setGenMsg] = useState('')
  async function onGenerateRoute() {
    if (!form.departure_port || !form.arrival_port) { setGenMsg('Enter both departure and arrival ports first.'); return }
    setGenBusy(true); setGenMsg('Geocoding ports…')
    try {
      const dep = await fetchWeatherByPort(form.departure_port)
      const arr = await fetchWeatherByPort(form.arrival_port)
      const draft = parseFloat(ukc.draft) || autoDraft || 3
      setGenMsg('Finding sea route around land…')
      const r = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'searoute', depLat: dep.latitude, depLon: dep.longitude, arrLat: arr.latitude, arrLon: arr.longitude, draft }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'routing failed')
      if (!d.ok) { setGenMsg(d.reason || 'No sea route found on the coarse grid.'); return }
      setRouteWaypoints(d.waypoints)
      setGenMsg('Generated offshore route: ' + d.waypoints.length + ' waypoints, starting and ending in open water clear of land (coarse GEBCO grid). Navigate berth\u2194offshore start/end on your ECDIS.')
    } catch (e) {
      setGenMsg('Could not generate route: ' + (e.message || 'failed'))
    } finally { setGenBusy(false) }
  }
  const [clearBusy, setClearBusy] = useState(false)
  const [clearMsg, setClearMsg] = useState('')
  // batch GEBCO depth lookup: returns array of depths (m, positive=below sea level) for given lon/lat arrays
  async function gebcoDepths(lons, lats) {
    const url = 'https://api.odb.ntu.edu.tw/gebco?mode=zonly&lon=' + lons.map(x => x.toFixed(4)).join(',') + '&lat=' + lats.map(x => x.toFixed(4)).join(',')
    const r = await fetch(url)
    const d = await r.json()
    const Z = d.z || []
    // densified; resample back to our N input points by even spacing
    return { z: Z }
  }
  async function onClearLand() {
    const wps = routeWaypoints
    if (wps.length < 2) { setClearMsg('Set a route first.'); return }
    setClearBusy(true); setClearMsg('')
    try {
      const draft = parseFloat(ukc.draft) || autoDraft || 3
      const minSafe = draft + 3 // metres of water needed to be "clear"
      // batch GEBCO depth for many points in ONE call (positive = depth below sea level)
      const depthsFor = async (pts) => {
        if (!pts.length) return []
        const lons = pts.map(p => p.lon.toFixed(4)).join(',')
        const lats = pts.map(p => p.lat.toFixed(4)).join(',')
        const u = 'https://api.odb.ntu.edu.tw/gebco?mode=zonly&lon=' + lons + '&lat=' + lats
        const r = await fetch(u); const d = await r.json()
        const Z = d.z || [], LO = d.longitude || [], LA = d.latitude || []
        // zonly densifies; map each requested point to the nearest returned sample
        return pts.map(p => {
          let bi = 0, bd = Infinity
          for (let j = 0; j < LO.length; j++) {
            const dd = Math.abs(LO[j] - p.lon) + Math.abs(LA[j] - p.lat)
            if (dd < bd) { bd = dd; bi = j }
          }
          const z = Z[bi]
          return (z != null) ? -z : null
        })
      }
      // does the leg a->b cross land/shallows? return the worst point if so
      const legWorst = async (a, b) => {
        const N = 14
        const pts = []
        for (let k = 1; k < N; k++) { const f = k / N; pts.push({ lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + (b.lon - a.lon) * f }) }
        const deps = await depthsFor(pts)
        let worst = null, wd = Infinity
        for (let k = 0; k < pts.length; k++) { const dep = deps[k]; if (dep != null && dep < wd) { wd = dep; worst = pts[k] } }
        return (worst && wd < minSafe) ? { point: worst, depth: wd } : null
      }
      // nudge a point perpendicular-seaward until clear; pick the deeper side
      const nudgeClear = async (worst, a, b) => {
        const dLat = b.lat - a.lat, dLon = b.lon - a.lon
        const len = Math.hypot(dLat, dLon) || 1e-9
        const perpLat = -dLon / len, perpLon = dLat / len
        let best = null, bestDepth = -Infinity
        for (let step = 1; step <= 10; step++) {
          const off = step * 0.025 // ~1.5nm per step, up to ~15nm
          const candA = { lat: worst.lat + perpLat * off, lon: worst.lon + perpLon * off }
          const candB = { lat: worst.lat - perpLat * off, lon: worst.lon - perpLon * off }
          const [dA, dB] = await depthsFor([candA, candB])
          if (dA != null && dA >= minSafe && dA > bestDepth) { best = { ...candA, name: 'Clear WP', locked: false }; bestDepth = dA }
          if (dB != null && dB >= minSafe && dB > bestDepth) { best = { ...candB, name: 'Clear WP', locked: false }; bestDepth = dB }
          if (best) break // first clear point at this distance wins
          // track deepest even if not yet clear
          if (dA != null && dA > bestDepth) { best = { ...candA, name: 'Clear WP', locked: false }; bestDepth = dA }
          if (dB != null && dB > bestDepth) { best = { ...candB, name: 'Clear WP', locked: false }; bestDepth = dB }
        }
        return best
      }
      // iterate: keep scanning + inserting until no leg crosses land, max passes
      let route = wps.slice()
      let inserted = 0
      const MAX_PASSES = 20
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        let changed = false
        const next = [route[0]]
        for (let i = 1; i < route.length; i++) {
          const a = route[i-1], b = route[i]
          const w = await legWorst(a, b)
          if (w) {
            const clear = await nudgeClear(w.point, a, b)
            if (clear) { next.push(clear); inserted++; changed = true }
          }
          next.push(b)
        }
        route = next
        if (!changed) break
        if (route.length > 40) break // safety cap
      }
      if (inserted > 0) {
        setRouteWaypoints(route)
        setClearMsg('Inserted ' + inserted + ' waypoint(s) to route around land/shallows (GEBCO ~500m grid — verify exact track on ECDIS).')
      } else {
        setClearMsg('No land or shallow crossings detected (GEBCO ~500m grid — still verify on ECDIS).')
      }
    } catch (e) {
      setClearMsg('Could not check route: ' + (e.message || 'failed'))
    } finally { setClearBusy(false) }
  }
  async function onDepthProfile() {
    const wps = routeWaypoints
    if (wps.length < 2) { setDepthMsg('Set a route first.'); return }
    setDepthBusy(true); setDepthMsg('')
    try {
      // sample ~60 evenly spaced points along the route (interpolate across legs)
      const rd = routeDistances(wps)
      const N = 60
      const lons = [], lats = []
      for (let i = 0; i < N; i++) {
        const f = i / (N - 1)
        const target = rd.total * f
        let leg = 1
        while (leg < rd.cum.length && rd.cum[leg] < target) leg++
        const a = wps[leg-1], b = wps[leg]
        const segStart = rd.cum[leg-1], segLen = rd.legs[leg-1] || 1e-9
        const lf = (target - segStart) / segLen
        lats.push(a.lat + (b.lat - a.lat) * lf)
        lons.push(a.lon + (b.lon - a.lon) * lf)
      }
      // STEP 1: GEBCO base for every sample point (one fast batch call, global)
      const profile = []
      let cum = 0
      {
        const url = 'https://api.odb.ntu.edu.tw/gebco?mode=zonly&lon=' + lons.map(x => x.toFixed(4)).join(',') + '&lat=' + lats.map(x => x.toFixed(4)).join(',')
        const r = await fetch(url)
        const d = await r.json()
        const Z = d.z || [], LO = d.longitude || [], LA = d.latitude || []
        if (!Z.length) throw new Error('no depth data returned')
        for (let i = 0; i < Z.length; i++) {
          if (i > 0) cum += nmBetween({ lat: LA[i-1], lon: LO[i-1] }, { lat: LA[i], lon: LO[i] })
          profile.push({ nm: cum, depth: (Z[i] != null) ? -Z[i] : null, src: 'gebco', lat: LA[i], lon: LO[i] })
        }
      }
      // STEP 2: overlay NOAA survey depth where available (US waters). Sample ~30 points to keep calls reasonable.
      let noaaHits = 0
      const midLat = lats[Math.floor(N/2)], midLon = lons[Math.floor(N/2)]
      const maybeUS = (midLon >= -177 && midLon <= -60 && midLat >= 15 && midLat <= 73)
      if (maybeUS && profile.length) {
        const sampleStep = Math.max(1, Math.floor(profile.length / 40))
        for (let i = 0; i < profile.length; i += sampleStep) {
          const pt = profile[i]
          try {
            const g = encodeURIComponent(JSON.stringify({ x: pt.lon, y: pt.lat }))
            const r = await fetch('https://gis.ngdc.noaa.gov/arcgis/rest/services/bag_bathymetry/ImageServer/identify?geometry=' + g + '&geometryType=esriGeometryPoint&returnGeometry=false&f=json')
            const j = await r.json()
            const v = parseFloat(j.value)
            if (!isNaN(v)) { pt.depth = -v; pt.src = 'noaa'; noaaHits++ }
          } catch (e) { /* keep gebco */ }
        }
      }
      const source = noaaHits > 0 ? ('NOAA survey (' + noaaHits + ' pts) + GEBCO') : 'GEBCO (~500m grid)'
      // downsample to ~120 points for a clean chart
      const step = Math.max(1, Math.floor(profile.length / 120))
      const slim = profile.filter((_, i) => i % step === 0)
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, depth_profile: { points: slim, total: cum, source, generatedAt: new Date().toISOString() } } }))
      setDepthMsg('Depth profile loaded — ' + source + ' (' + slim.filter(x => x.depth != null).length + ' points).')
    } catch (e) {
      setDepthMsg('Could not load depth profile: ' + (e.message || 'fetch failed'))
    } finally { setDepthBusy(false) }
  }
  const [portBusy, setPortBusy] = useState(false)
  async function onAddContact(portKey, contact) {
    try {
      const r = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'portcontacts', mode: 'add', client_id: config?.id || authConfig?.id, contact: { ...contact, port_name: portKey } }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'add failed')
      // reload contacts for this port
      const cr = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'portcontacts', mode: 'list', port: portKey }) })
      const cd = await cr.json()
      const contacts = cr.ok ? (cd.contacts || []) : []
      setForm(f => {
        const pi = f.auto_data?.port_info
        if (!pi) return f
        const results = pi.results.map(res => res.portKey === portKey ? { ...res, contacts } : res)
        return { ...f, auto_data: { ...f.auto_data, port_info: { results } } }
      })
      return true
    } catch (e) { return false }
  }
  const [portMsg, setPortMsg] = useState('')
  async function onPortInfo() {
    const rawPorts = [form.departure_port, form.arrival_port].filter(Boolean)
    if (!rawPorts.length) { setPortMsg('Enter departure and/or arrival ports first.'); return }
    setPortBusy(true); setPortMsg('Locating ports…')
    try {
      // geocode each port so we can match WPI by coordinates if the name differs
      const ports = []
      for (const nm of rawPorts) {
        try { const g = await fetchWeatherByPort(nm); ports.push({ name: nm, lat: g.latitude, lon: g.longitude }) }
        catch { ports.push({ name: nm }) }
      }
      const r = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'portinfo', ports }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'lookup failed')
      const found = (d.results || []).filter(x => x.found)
      // also load shared contacts for each port found
      const withContacts = []
      for (const res of (d.results || [])) {
        let contacts = []
        const pn = res.found && res.port ? res.port.portName : res.query
        try {
          const cr = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'portcontacts', mode: 'list', port: pn }) })
          const cd = await cr.json()
          if (cr.ok) contacts = cd.contacts || []
        } catch {}
        withContacts.push({ ...res, contacts, portKey: pn })
      }
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, port_info: { results: withContacts } } }))
      setPortMsg(found.length ? (found.length + ' port(s) found in the World Port Index.') : 'No matching ports in the World Port Index. Try the official port name.')
    } catch (e) {
      setPortMsg('Could not load port info: ' + (e.message || 'failed'))
    } finally { setPortBusy(false) }
  }
  const [warnBusy, setWarnBusy] = useState(false)
  const [warnMsg, setWarnMsg] = useState('')
  const [whaleBusy, setWhaleBusy] = useState(false)
  const [whaleMsg, setWhaleMsg] = useState('')
  async function onWhaleCheck() {
    const wps = routeWaypoints
    if (wps.length < 2) { setWhaleMsg('Set a route first.'); return }
    setWhaleBusy(true); setWhaleMsg('')
    try {
      const route = wps.map(w => ({ lat: w.lat, lon: w.lon }))
      const r = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'whalezones', route }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'check failed')
      const crossed = d.crossed || []
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, whale_zones: { crossed, checkedAt: new Date().toISOString() } } }))
      setWhaleMsg(crossed.length ? (crossed.length + ' whale speed zone(s) on your route.') : 'No right-whale speed zones on your route (US East Coast SMAs).')
    } catch (e) {
      setWhaleMsg('Could not check whale zones: ' + (e.message || 'failed'))
    } finally { setWhaleBusy(false) }
  }
  async function onNavWarnings() {
    const wps = routeWaypoints
    if (wps.length < 2) { setWarnMsg('Set a route first.'); return }
    setWarnBusy(true); setWarnMsg('')
    try {
      const r = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'navwarnings' }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'fetch failed')
      const all = d.warnings || []
      // keep warnings with at least one position within ~50nm of any route leg
      const NEAR_NM = 50
      const near = []
      for (const w of all) {
        if (!w.positions || !w.positions.length) continue
        let minD = Infinity
        for (const pos of w.positions) {
          for (let i = 1; i < wps.length; i++) {
            const d2 = distToSegmentNm(pos, wps[i-1], wps[i])
            if (d2 < minD) minD = d2
          }
        }
        if (minD <= NEAR_NM) near.push({ ...w, distNm: Math.round(minD) })
      }
      near.sort((a, b) => a.distNm - b.distNm)
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, nav_warnings: { items: near, total: all.length, fetchedAt: d.fetchedAt } } }))
      setWarnMsg(near.length ? (near.length + ' warning(s) near your route (of ' + all.length + ' active worldwide).') : 'No active warnings within ' + NEAR_NM + ' NM of your route (of ' + all.length + ' worldwide).')
    } catch (e) {
      setWarnMsg('Could not load warnings: ' + (e.message || 'failed'))
    } finally { setWarnBusy(false) }
  }
  function setSpeed(v) { setForm(f => ({ ...f, auto_data: { ...f.auto_data, speed_kn: v } })) }



  function setRouteWaypoints(next) {
    // re-lock endpoints, keep first/last as ports
    const fixed = next.map((w, i) => ({ ...w, locked: (i === 0 || i === next.length - 1) }))
    setForm(f => ({ ...f, auto_data: { ...f.auto_data, route_waypoints: fixed } }))
  }

  // re-fetch weather along the *custom* route waypoints
  async function updateRouteWeather() {
    setWxLoading(true); setWxErr('')
    try {
      const wps = routeWaypoints
      if (wps.length < 2) throw new Error('Set a route first (Fetch weather to seed it).')
      const enriched = []
      let wpNum = 0
      for (let i = 0; i < wps.length; i++) {
        const w = wps[i]
        const f = i / (wps.length - 1)
        const isEnd = (i === 0 || i === wps.length - 1)
        // label: ports keep their name; intermediates are numbered sequentially
        let label
        if (isEnd) label = w.name || (i === 0 ? 'Departure' : 'Arrival')
        else { wpNum += 1; label = 'Waypoint ' + wpNum }
        const wd = await fetchWeatherByCoord(w.lat, w.lon, label)
        const sum = summariseWx(wd, label, etaAt(f)) || { label, current: {}, days: [] }
        sum.f = f
        sum.lat = w.lat; sum.lon = w.lon; sum.locked = w.locked
        enriched.push(sum)
      }
      const dep = enriched[0], arr = enriched[enriched.length - 1]
      const mids = enriched.slice(1, -1)
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, weather: {
        ...f.auto_data.weather,
        departure: dep, arrival: arr, waypoints: mids,
        route: { ...(f.auto_data.weather?.route || {}), midLat: (dep.lat + arr.lat) / 2, midLon: (dep.lon + arr.lon) / 2 },
        fetchedAt: new Date().toISOString(),
      } } }))
    } catch (e) {
      setWxErr(e?.message || 'Route weather update failed')
    } finally { setWxLoading(false) }
  }

  // geocode a port name -> {lat, lon, location, current, days}
  async function fetchWeatherByPort(portName) {
    const r = await fetch('/api/weather?port=' + encodeURIComponent(portName))
    const d = await r.json()
    if (!r.ok || d.latitude == null) throw new Error('Could not locate ' + portName)
    return d
  }
  // marine weather at an explicit position (interpolated waypoints have no name)
  async function fetchWeatherByCoord(lat, lon, label) {
    const r = await fetch('/api/weather?lat=' + lat + '&lon=' + lon + '&name=' + encodeURIComponent(label || ''))
    const d = await r.json()
    if (!r.ok || !d.current) return null
    return d
  }
  function summariseWx(d, label, etaISO) {
    if (!d) return null
    const c = d.current || {}
    const days = (d.days || []).slice(0, 7).map(x => ({
      date: x.date, conditions: x.conditions, windMaxKn: x.windMaxKn,
      windCompass: x.windCompass, gustMaxKn: x.gustMaxKn, waveMaxM: x.waveMaxM, swellMaxM: x.swellMaxM,
    }))
    // pick the forecast day matching the ETA (by calendar date), if available
    let etaForecast = null
    if (etaISO) {
      // match by LOCAL calendar date, not the UTC ISO slice (avoids day-slip near midnight in +ve timezones)
      const e = new Date(etaISO)
      const etaDay = !isNaN(e.getTime())
        ? e.getFullYear() + '-' + String(e.getMonth() + 1).padStart(2, '0') + '-' + String(e.getDate()).padStart(2, '0')
        : etaISO.split('T')[0]
      etaForecast = days.find(x => (x.date || '').split('T')[0] === etaDay) || null
    }
    let etaHour = null
    if (etaISO && Array.isArray(d.hourly) && d.hourly.length) {
      const et = new Date(etaISO).getTime()
      let best = null, bestDiff = Infinity
      for (const h of d.hourly) { const t = new Date(h.time).getTime(); const diff = Math.abs(t - et); if (!isNaN(t) && diff < bestDiff) { best = h; bestDiff = diff } }
      if (best && bestDiff <= 3 * 3600e3) etaHour = { time: best.time, windKn: best.windKn, windCompass: best.windCompass, gustKn: best.gustKn, waveM: best.waveM, swellM: best.swellM, conditions: best.conditions }
    }
    return {
      label, location: d.location || label,
      lat: d.latitude, lon: d.longitude,
      eta: etaISO || null,
      etaForecast,
      etaHour,
      current: {
        tempC: c.tempC, conditions: c.conditions,
        windKn: c.windKn, windCompass: c.windCompass, gustKn: c.gustKn,
        waveM: c.waveM, swellM: c.swellM,
        visibilityKm: c.visibilityM != null ? Math.round(c.visibilityM / 1000) : null,
      },
      days,
      hourly: (d.hourly || []).map(h => ({
        time: h.time, windKn: h.windKn, windCompass: h.windCompass, gustKn: h.gustKn,
        waveM: h.waveM, swellM: h.swellM, conditions: h.conditions,
      })),
    }
  }

  // interpolate an ETA between departure and arrival datetimes for fraction f (0..1)
  function etaAt(f) {
    if (!form.departure_date) return null
    const depDT = new Date(form.departure_date + 'T' + (form.departure_time_local || '00:00'))
    if (isNaN(depDT.getTime())) return null
    // if speed + distance known, time = departure + (cumulative distance at fraction f / speed)
    const sp = parseFloat(form.auto_data?.speed_kn) || null
    const d = routeDistances(routeWaypoints)
    if (sp && d.total) {
      const nmAtF = d.total * f
      const hrs = nmAtF / sp
      return new Date(depDT.getTime() + hrs * 3600000).toISOString()
    }
    // else fall back to linear across departure->arrival
    let arrDT = (form.arrival_date) ? new Date(form.arrival_date + 'T' + (form.arrival_time_local || '00:00')) : null
    if (!arrDT || isNaN(arrDT.getTime()) || arrDT <= depDT) return depDT.toISOString()
    const ms = depDT.getTime() + (arrDT.getTime() - depDT.getTime()) * f
    return new Date(ms).toISOString()
  }

  async function fetchWeather() {
    setWxLoading(true); setWxErr('')
    try {
      if (!form.departure_port || !form.arrival_port) throw new Error('Enter both departure and arrival ports first.')
      const depD = await fetchWeatherByPort(form.departure_port)
      const arrD = await fetchWeatherByPort(form.arrival_port)
      const lat1 = depD.latitude, lon1 = depD.longitude, lat2 = arrD.latitude, lon2 = arrD.longitude
      const STEPS = 4 // 3 intermediate points along the direct route
      const waypoints = []
      for (let i = 1; i < STEPS; i++) {
        const f = i / STEPS
        const wlat = lat1 + (lat2 - lat1) * f
        const wlon = lon1 + (lon2 - lon1) * f
        const wd = await fetchWeatherByCoord(wlat, wlon, 'WP' + i)
        const wp = summariseWx(wd, 'Waypoint ' + i, etaAt(f)) || { label: 'Waypoint ' + i, lat: wlat, lon: wlon, eta: etaAt(f), current: {}, days: [] }
        wp.lat = wlat; wp.lon = wlon
        waypoints.push(wp)
      }
      const midLat = (lat1 + lat2) / 2, midLon = (lon1 + lon2) / 2
      setForm(f => ({ ...f, auto_data: { ...f.auto_data, weather: {
        departure: summariseWx(depD, form.departure_port, etaAt(0)),
        arrival: summariseWx(arrD, form.arrival_port, etaAt(1)),
        waypoints,
        route: { lat1, lon1, lat2, lon2, midLat, midLon },
        fetchedAt: new Date().toISOString(),
      } } }))
    } catch (e) {
      setWxErr(e?.message || 'Weather lookup failed')
    } finally { setWxLoading(false) }
  }

  // ============================================================
  // SIGNERS + PDF EXPORT
  // ============================================================
  // signer roles: Captain, Chief Officer, then each selected watch leader
  const signers = useMemo(() => {
    const base = [{ role: 'Captain', key: 'sig-captain' }, { role: 'Chief Officer', key: 'sig-chief' }]
    const wl = (form.watch_leaders || []).map((n, i) => ({ role: n, key: 'sig-wl-' + i }))
    return [...base, ...wl]
  }, [form.watch_leaders])

  const [exporting, setExporting] = useState(false)

  // ---- printable passage plan (clean A4 layout with the Hey Captain header) ----
  function buildPrintHtml(sigData) {
    const NAVY = '#0A1628', GOLD = '#C9A84C', LINE = '#E5E9F1', MUTE = '#67748C', TEXT = '#16233B', BLUE = '#1155F5'
    const ad = form.auto_data || {}
    const e = escHtml
    const fmtD = iso => { if (!iso) return '\u2013'; const d = new Date(iso); return isNaN(d) ? e(iso) : d.toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) }
    const dateOnly = s => { if (!s) return '\u2013'; const d = new Date(s + 'T12:00:00'); return isNaN(d) ? e(s) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }
    const sec = (title, body) => body ? '<div style="margin-top:14px;page-break-inside:avoid;"><div style="font-size:10px;font-weight:800;letter-spacing:.12em;color:' + GOLD + ';text-transform:uppercase;border-bottom:1.5px solid ' + GOLD + ';padding-bottom:3px;margin-bottom:7px;">' + e(title) + '</div>' + body + '</div>' : ''
    const tbl = (head, rows) => '<table style="width:100%;border-collapse:collapse;font-size:9.5px;"><thead><tr>' + head.map(h => '<th style="text-align:left;padding:4px 6px;background:' + NAVY + ';color:#fff;font-weight:600;">' + h + '</th>').join('') + '</tr></thead><tbody>' +
      rows.map((r, i) => '<tr style="background:' + (i % 2 ? '#F7F9FC' : '#fff') + ';">' + r.map(c => '<td style="padding:4px 6px;border-bottom:1px solid ' + LINE + ';vertical-align:top;">' + (c == null ? '\u2013' : c) + '</td>').join('') + '</tr>').join('') + '</tbody></table>'
    const kv = pairs => '<div style="display:flex;flex-wrap:wrap;row-gap:8px;">' + pairs.map(([k, v]) => '<div style="width:25%;box-sizing:border-box;padding-right:10px;"><div style="font-size:8px;color:' + MUTE + ';text-transform:uppercase;letter-spacing:.06em;">' + e(k) + '</div><div style="font-size:11px;font-weight:700;color:' + TEXT + ';">' + (v == null || v === '' ? '\u2013' : e(v)) + '</div></div>').join('') + '</div>'

    // 1. summary
    const passTxt = passageHours ? fmtHrs(passageHours) : '\u2013'
    const summary = kv([
      ['Departure', form.departure_port], ['Date', dateOnly(form.departure_date)], ['Time (local)', toHHMM(form.departure_time_local)], ['Time (UTC)', depUtc ? (String(depUtc).length <= 5 ? String(depUtc) : String(depUtc).slice(11, 16)) : ''],
      ['Arrival', form.arrival_port], ['Date', dateOnly(form.arrival_date)], ['Time (local)', form.arrival_time_local ? toHHMM(form.arrival_time_local) : ''], ['Time (UTC)', arrUtc ? (String(arrUtc).length <= 5 ? String(arrUtc) : String(arrUtc).slice(11, 16)) : ''],
      ['Distance', dist.total ? dist.total.toFixed(1) + ' NM' : ''], ['Planned speed', speedKn ? speedKn + ' kn' : ''], ['Passage time', passTxt], ['Watch leaders', (form.watch_leaders || []).join(', ')],
    ])

    // 2. route & weather at each point's ETA
    const wx = ad.weather
    let weatherHtml = ''
    if (wx && (wx.departure || (wx.waypoints || []).length)) {
      const seq = [wx.departure, ...(wx.waypoints || []), wx.arrival].filter(Boolean)
      const rw = routeWaypoints || []
      const rd = rw.length >= 2 ? routeDistances(rw) : null
      const rows = seq.map(w => {
        let fr = w === wx.departure ? 0 : w === wx.arrival ? 1 : (w.f != null ? w.f : null)
        if (fr == null && rd && rd.total && w.lat != null) fr = alongTrackFrac(rw, w.lat, w.lon)
        const eta = fr != null ? etaAt(fr) : w.eta
        let h = null
        if (eta && Array.isArray(w.hourly)) { const et = new Date(eta).getTime(); let bd = Infinity; for (const x of w.hourly) { const d = Math.abs(new Date(x.time).getTime() - et); if (d < bd) { bd = d; h = x } } if (bd > 3 * 3600e3) h = null }
        const c = h || w.current || {}
        const pos = w.lat != null ? Math.abs(w.lat).toFixed(3) + '\u00b0' + (w.lat >= 0 ? 'N' : 'S') + ' ' + Math.abs(w.lon).toFixed(3) + '\u00b0' + (w.lon >= 0 ? 'E' : 'W') : ''
        return ['<b>' + e(w.location || w.label) + '</b>', pos, fmtD(eta), c.windKn != null ? Math.round(c.windKn) + ' kn ' + e(c.windCompass || '') : null, c.gustKn != null ? Math.round(c.gustKn) + ' kn' : null, c.waveM != null ? c.waveM + ' m' : null, c.swellM != null ? c.swellM + ' m' : null, e(c.conditions || '')]
      })
      weatherHtml = tbl(['Point', 'Position', 'ETA', 'Wind', 'Gust', 'Sea', 'Swell', 'Conditions'], rows) + '<div style="font-size:8px;color:' + MUTE + ';margin-top:3px;">Forecast at each point for the ETA (nearest 3-hour forecast slot).</div>'
    }
    const legs = (routeWaypoints || []).length >= 2 ? (() => { const rd = routeDistances(routeWaypoints); return tbl(['#', 'Latitude', 'Longitude', 'Leg (NM)', 'Total (NM)'], routeWaypoints.map((p, i) => [String(i + 1), p.lat.toFixed(4), p.lon.toFixed(4), i ? (rd.legs[i - 1] || 0).toFixed(1) : '\u2013', (rd.cum[i] || 0).toFixed(1)])) })() : ''

    // 3. tides + UKC
    const tideRow = t => t ? '<div style="margin:3px 0;"><b>' + e(t.port) + '</b>' + (t.tidalRangeM != null ? ' \u00b7 range ' + t.tidalRangeM + ' m' : '') + '<div style="font-size:9px;color:' + MUTE + ';">' + (t.extremes || []).map(x => e(x.type) + ' ' + e(x.time) + ' (' + x.heightM + ' m)').join(' \u00b7 ') + '</div></div>' : ''
    const tides = ad.tides ? tideRow(ad.tides.departure) + tideRow(ad.tides.arrival) : ''
    const u = ad.ukc || {}
    const ukcHtml = (u.draft || u.charted_depth) ? kv([['Draft', u.draft ? u.draft + ' m' : ''], ['Charted depth (shallowest)', u.charted_depth ? u.charted_depth + ' m' : ''], ['Tide height', u.tide_height ? u.tide_height + ' m' : 'lowest fetched'], ['UKC', ukcResult != null ? ukcResult + ' m' : '']]) : ''

    // 4. fuel
    const fd = ad.fuel || {}
    let fuelHtml = ''
    if (fd.options && fd.options.some(o => o.speed && o.lph)) {
      const fob = parseFloat(fd.on_board) || 0, unit = fd.unit || 'L', names = ['Economy', 'Cruise', 'Fast']
      const n0 = v => v == null || !isFinite(v) ? '\u2013' : Math.round(v).toLocaleString('en-GB')
      fuelHtml = kv([['Fuel on board', fob ? n0(fob) + ' ' + unit : ''], ['Reserve', (fd.reserve_pct ?? 10) + ' %'], ['Planned setting', fd.selected != null ? names[fd.selected] : ''], ['Actual used', fd.on_arrival_actual ? n0(fob - parseFloat(fd.on_arrival_actual)) + ' ' + unit : '']]) + '<div style="height:6px"></div>' +
        tbl(['Setting', 'Speed', 'RPM', unit + '/h', 'Passage', 'Fuel for passage', 'Left on arrival', 'Endurance', 'Range'], fd.options.map((o, i) => {
          const sp = parseFloat(o.speed), l = parseFloat(o.lph), hrs = sp && dist.total ? dist.total / sp : null, used = hrs && l ? hrs * l : null
          const b = x => i === fd.selected ? '<b>' + e(x) + '</b>' : e(x)   // typed speed / rpm / l/h (zap-active)
          return [b(names[i]), b(o.speed ? o.speed + ' kn' : '\u2013'), b(o.rpm || '\u2013'), b(o.lph || '\u2013'), b(hrs ? fmtHrs(hrs) : '\u2013'), b(used ? n0(used) + ' ' + unit : '\u2013'), b(used != null && fob ? n0(fob - used) + ' ' + unit : '\u2013'), b(l && fob ? fmtHrs(fob / l) : '\u2013'), b(l && fob && sp ? n0(fob / l * sp) + ' NM' : '\u2013')]
        }))
    }

    // 5. nav warnings, ports, regulations
    const nw = (ad.nav_warnings?.items || [])
    const nwHtml = nw.length ? nw.map(w => '<div style="margin:4px 0;padding:5px 7px;border:1px solid ' + LINE + ';border-radius:4px;"><div style="font-size:8.5px;font-weight:700;color:#8a5c0a;">' + e(w.navArea) + ' ' + e(w.msgNumber) + '/' + e(w.msgYear) + ' \u00b7 ~' + e(w.distNm) + ' NM</div><div style="font-size:8.5px;font-family:monospace;white-space:pre-line;">' + e(String(w.text || '').slice(0, 420)) + (String(w.text || '').length > 420 ? '\u2026' : '') + '</div></div>').join('') : ''
    const ports = (ad.port_info?.results || []).filter(r => r.found && r.port)
    const yn = v => v === 'Y' ? 'Yes' : v === 'N' ? 'No' : '\u2013'
    const portsHtml = ports.length ? tbl(['Port', 'Depths (channel / anchorage / pier)', 'Pilotage', 'Supplies', 'Repairs', 'ETA message'], ports.map(r => { const p = r.port; return ['<b>' + e(p.portName || r.portKey || '') + '</b>', [p.chDepth, p.anDepth, p.cpDepth].map(x => x ? x + ' m' : '\u2013').join(' / '), p.ptCompulsory === 'Y' ? 'Compulsory' : p.ptAvailable === 'Y' ? 'Available' : '\u2013', [p.suFuel === 'Y' && 'Fuel', p.suDiesel === 'Y' && 'Diesel', p.suWater === 'Y' && 'Water', p.suProvisions === 'Y' && 'Provisions'].filter(Boolean).join(', ') || '\u2013', e(p.repairCode || '\u2013'), yn(p.etaMessage)] })) : ''
    const regs = ad.applicable_regs
    const regsHtml = regs && regs.answer ? '<div style="font-size:9.5px;line-height:1.45;">' + mdToHtml(regs.answer) + '</div>' + ((regs.sources || []).length ? '<div style="font-size:8.5px;color:' + MUTE + ';margin-top:4px;">Sources: ' + regs.sources.map(s => e(typeof s === 'string' ? s : (s.title || s.name || s.file || ''))).join(' \u00b7 ') + '</div>' : '') + '<div style="font-size:8px;color:' + MUTE + ';font-style:italic;margin-top:3px;">AI-assisted guidance \u2014 verify against the source documents.</div>' : ''

    // 6. checklist answers
    const ans = v => v === 'yes' ? '<span style="color:#137050;font-weight:700;">Yes</span>' : v === 'no' ? '<span style="color:#C93B47;font-weight:700;">No</span>' : v === 'na' ? '<span style="color:' + MUTE + ';">N/A</span>' : '<span style="color:#C93B47;">\u2013</span>'
    const ynTbl = (items, data) => tbl(['Check', 'Answer'], items.map(([k, label]) => [e(label), ans((data || {})[k])]))
    const checks = [['Charts & Publications', CHARTS_ITEMS, form.charts_publications], ['Tidal Information', TIDAL_ITEMS, form.tidal_info], ['Weather & Marine Safety', WEATHER_ITEMS, form.weather_msi], ['Routing & Reporting', ROUTING_ITEMS, form.routing_reporting], ['Bridge Equipment', BRIDGE_ITEMS, form.bridge_equipment]]
      .map(([t, items, data]) => '<div style="font-size:9.5px;font-weight:700;margin:6px 0 3px;">' + t + '</div>' + ynTbl(items, data)).join('')

    // 7. before departure
    const pd = ad.predep || {}
    const pdTicks = pd.ticks || {}
    const pdItems = [['crew_list', 'Crew list prepared for port clearance / agent'], ['deck', 'Deck pre-departure checklist (PD-01)'], ['engine', 'Engine room pre-departure checklist (PD-02)'], ['weather', 'Weather checked for route and destination'], ['briefing', 'Crew and guests briefed on passage and weather'], ['port', 'Port / marina / agent informed of departure time'], ['signed', 'Passage plan approved and signed by the Master'], ...((pd.custom || []).map((c, i) => ['c' + i, c]))]
    const pdAuto = { weather: !!ad.weather, signed: (sigData || []).some(x => x.signature) }
    const predepHtml = tbl(['Task', 'Status'], pdItems.map(([k, label]) => { const t = pdTicks[k]; const done = pdAuto[k] || (t && t.done); return [e(label), done ? '<span style="color:#137050;font-weight:700;">\u2713 Done</span>' + (t && t.at ? ' <span style="color:' + MUTE + ';">' + fmtD(t.at) + '</span>' : '') : '<span style="color:#C93B47;">Open</span>'] }))

    // 8. watch bill (saved grid)
    const wb = ad.watch_bill
    let wbHtml = ''
    if (wb && wb.rows && wb.rows.length && wb.days) {
      wbHtml = (wb.ok != null ? '<div style="font-size:9.5px;font-weight:700;color:' + (wb.ok ? '#137050' : '#C93B47') + ';margin-bottom:4px;">' + (wb.ok ? 'MLC compliant \u00b7 bridge covered every hour' : 'NOT MLC compliant \u2014 review the watch bill') + '</div>' : '') +
        wb.days.map(d => '<div style="font-size:8.5px;font-weight:700;color:' + MUTE + ';margin:4px 0 2px;">' + e(d) + '</div><table style="width:100%;border-collapse:collapse;"><tr><th style="text-align:left;font-size:7.5px;padding:2px 4px;background:' + NAVY + ';color:#fff;">Crew</th>' +
          Array.from({ length: 24 }, (_, h) => '<th style="font-size:6.5px;padding:2px 0;background:' + NAVY + ';color:#fff;">' + String(h).padStart(2, '0') + '</th>').join('') + '</tr>' +
          wb.rows.map(r => '<tr><td style="font-size:7.5px;padding:1px 4px;white-space:nowrap;">' + e(r.name) + ' <span style="color:' + MUTE + ';">' + (r.kind === 'leader' ? 'WL' : r.kind === 'lookout' ? 'LO' : '') + '</span></td>' +
            String((r.cells || {})[d] || '').split('').map(v => '<td style="height:10px;border:1px solid #fff;background:' + (v === 'W' ? (r.kind === 'leader' ? 'rgba(17,85,245,.65)' : r.kind === 'lookout' ? 'rgba(23,138,92,.45)' : 'rgba(201,168,76,.35)') : 'rgba(201,59,71,.06)') + ';"></td>').join('') + '</tr>').join('') + '</table>').join('')
    }

    // 9. standing orders + acknowledgements
    const so = ad.standing_orders
    const soDocs = (so && so.docs && so.docs.length) ? so.docs : SO_DEFAULTS
    const ack = (so && so.ack) || {}
    const people = [...(form.watch_leaders || []).map(n => [n, 'Watch leader']), ...((wb && wb.lookouts) || []).map(n => [n, 'Lookout'])].filter((p, i, a) => a.findIndex(q => q[0] === p[0]) === i)
    const soHtml = soDocs.map(d => '<div style="margin:6px 0 10px;page-break-inside:avoid;"><div style="font-size:11px;font-weight:800;color:' + NAVY + ';margin-bottom:3px;">' + e(d.title) + '</div><div style="font-size:9.5px;line-height:1.45;">' + soToHtml(d.body) + '</div></div>').join('') +
      (people.length ? '<div style="font-size:9.5px;font-weight:700;margin:6px 0 3px;">Read and understood</div>' + tbl(['Name', 'Role', 'Acknowledged'], people.map(([n, r]) => [e(n), r, ack[n] ? '<span style="color:#137050;font-weight:700;">\u2713</span> ' + fmtD(ack[n]) : '<span style="color:#C93B47;">not yet</span>'])) : '')

    // 10. signatures
    const sigHtml = '<div style="display:flex;flex-wrap:wrap;gap:10px;">' + (sigData || []).map(sg => '<div style="width:calc(50% - 5px);box-sizing:border-box;border:1px solid ' + LINE + ';border-radius:6px;padding:8px;page-break-inside:avoid;"><div style="font-size:10px;font-weight:700;">' + e(sg.role) + '</div>' + (sg.signature && sg.signature.length > 200 ? '<img src="' + e(sg.signature) + '" style="height:54px;max-width:100%;object-fit:contain;display:block;margin:4px 0;" />' : '<div style="height:54px;"></div>') + '<div style="font-size:8.5px;color:' + MUTE + ';border-top:1px solid ' + LINE + ';padding-top:3px;">Date: ' + e(sg.date || '') + '</div></div>').join('') + '</div>'

    const wave = '<span style="display:inline-block;font-size:26px;font-weight:900;color:' + BLUE + ';line-height:1;letter-spacing:-3px;">\u2248</span>'
    const vessel = config?.vessel_name || 'Vessel'
    return '<div style="width:100%;max-width:100%;box-sizing:border-box;overflow:hidden;padding:4px 6px;font-family:Inter,Helvetica,Arial,sans-serif;color:' + TEXT + ';background:#fff;">' +
      '<div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid ' + GOLD + ';padding-bottom:8px;">' +
        '<div><div style="display:flex;align-items:center;gap:10px;">' + wave + '<span style="font-size:18px;font-weight:800;letter-spacing:.13em;color:' + NAVY + ';">HEYCAPTAIN</span></div>' +
        '<div style="font-size:8.5px;color:' + MUTE + ';letter-spacing:.2em;text-transform:uppercase;margin-top:3px;margin-left:36px;">Passage Plan \u00b7 SOLAS V</div></div>' +
        '<div style="text-align:right;max-width:55%;word-break:break-word;"><div style="font-size:14px;font-weight:800;color:' + NAVY + ';">' + e(vessel) + '</div><div style="font-size:12px;font-weight:700;">' + e(form.departure_port || '') + ' \u2192 ' + e(form.arrival_port || '') + '</div><div style="font-size:9px;color:' + MUTE + ';">' + dateOnly(form.departure_date) + ' \u00b7 ' + ((sigData || []).some(x => x.signature) ? 'Signed' : 'Draft') + '</div></div>' +
      '</div>' +
      sec('Passage summary', summary) +
      sec('Route & weather at ETA', weatherHtml) + sec('Waypoints', legs) +
      sec('Tides & under-keel clearance', tides + (ukcHtml ? '<div style="height:6px"></div>' + ukcHtml : '')) +
      sec('Fuel plan', fuelHtml) + sec('Navigational warnings near the route', nwHtml) + sec('Ports', portsHtml) + sec('Applicable regulations', regsHtml) +
      sec('Passage checklist', checks) + sec('Before departure', predepHtml) +
      sec('Additional notes', form.additional_notes ? '<div style="font-size:9.5px;white-space:pre-wrap;line-height:1.45;">' + e(form.additional_notes) + '</div>' : '') +
      sec('Watch bill (underway)', wbHtml) + sec('Standing orders & watch guidelines', soHtml) + sec('Sign-off', sigHtml) +
      '<div style="margin-top:14px;padding-top:6px;border-top:1px solid ' + LINE + ';display:flex;justify-content:space-between;font-size:8px;color:' + MUTE + ';"><span>Generated by Hey Captain \u00b7 heycaptain.io</span><span>' + new Date().toLocaleString('en-GB') + '</span></div>' +
    '</div>'
  }


  async function signAndExport() {
    setExporting(true)
    try {
      // 1. collect signature data URLs from the canvases
      const sigData = signers.map(sgn => {
        const c = document.getElementById(sgn.key)
        let dataUrl = ''
        try { dataUrl = c ? c.toDataURL('image/png') : '' } catch { dataUrl = '' }
        return { role: sgn.role, signature: dataUrl, date: todayISO() }
      })

      // 2. render the printable block to a PDF blob (portrait A4)
      const html = buildPrintHtml(sigData)
      const safeTitle = (form.title || (form.departure_port + '_' + form.arrival_port) || 'passage_plan').replace(/[^a-zA-Z0-9]/g, '_')
      const opt = { margin: 8, filename: safeTitle + '_Passage_Plan.pdf', image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 780 }, pagebreak: { mode: ['css', 'legacy'] }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
      let publicUrl = ''
      if (html) {
        const blob = await html2pdf().set(opt).from(html, 'string').outputPdf('blob')
        // also download the signed PDF straight away (it is archived below as well)
        try { const dl = document.createElement('a'); dl.href = URL.createObjectURL(blob); dl.download = opt.filename; document.body.appendChild(dl); dl.click(); setTimeout(() => { URL.revokeObjectURL(dl.href); dl.remove() }, 2000) } catch (e) {}
        // 3. upload to supabase storage
        const path = filePath('passage_plans', config?.id, safeTitle + '.pdf')
        const { error: upErr } = await supabase.storage.from('crew-docs').upload(path, blob, { contentType: 'application/pdf', upsert: false })
        if (!upErr) publicUrl = path   // private bucket: the record keeps the PATH, opened through a signed link
      }

      // 4. persist signatures + pdf_url + status='signed'
      const links = await syncPassageLinks(true)
      const payload = {
        client_id: config?.id,
        vessel_name: config?.vessel_name || '',
        title: form.title || (form.departure_port + ' \u2192 ' + form.arrival_port),
        departure_port: form.departure_port, departure_date: form.departure_date || null,
        departure_time_local: form.departure_time_local, departure_time_utc: depUtc,
        arrival_port: form.arrival_port, arrival_date: form.arrival_date || null,
        arrival_time_local: form.arrival_time_local, arrival_time_utc: arrUtc,
        watch_leaders: form.watch_leaders,
        charts_publications: form.charts_publications, tidal_info: form.tidal_info,
        weather_msi: form.weather_msi, routing_reporting: form.routing_reporting,
        port_contacts: form.port_contacts, bridge_equipment: form.bridge_equipment,
        additional_notes: form.additional_notes, auto_data: { ...(form.auto_data || {}), links },
        signatures: sigData, status: 'signed', pdf_url: publicUrl, created_by: user?.id,
      }
      let savedId = form.id
      if (form.id) await supabase.from('passage_plans').update(payload).eq('id', form.id)
      else { const { data } = await supabase.from('passage_plans').insert(payload).select('id').single(); savedId = data?.id; if (form.trip_id && savedId) await linkTripToPlan(form.trip_id, savedId) }
      await loadPlans()
      setForm(f => ({ ...f, id: savedId, signatures: sigData, pdf_url: publicUrl, auto_data: { ...(f.auto_data || {}), links } }))
      alert('Passage plan signed and exported.' + (publicUrl ? '' : ' (PDF archive skipped — storage error)'))
    } catch (e) {
      alert('Export failed: ' + (e?.message || e))
    } finally { setExporting(false) }
  }

  // ============================================================
  // RENDER
  // ============================================================
  if (loading) return <div style={st.muted}>Loading…</div>

  if (view === 'list') {
    return (
      <div className="fade-up">
        <div style={st.headRow}>
          <div>
            <div style={st.h2}>Passage Plans</div>
            <div style={st.sub}>SOLAS V voyage planning — appraisal, planning, execution & monitoring</div>
          </div>
          <button style={st.primary} onClick={() => { setForm(blankForm()); setView('edit') }}>+ New Passage Plan</button>
        </div>
        {plans.length === 0 && <div style={st.empty}>No passage plans yet. Create your first one.</div>}
        {plans.map(p => (
          <div key={p.id} style={st.planCard}>
            <div style={{ flex: 1 }}>
              <div style={st.planTitle}>{p.title || (p.departure_port + ' → ' + p.arrival_port)}</div>
              <div style={st.planMeta}>
                {p.departure_port} → {p.arrival_port}
                {p.departure_date && ' · ' + p.departure_date}
                {' · '}<span style={{ color: p.status === 'signed' ? '#137050' : 'var(--muted)' }}>{p.status}</span>
              </div>
            </div>
            <button style={st.btn} onClick={() => editPlan(p)}>Open</button>
            <button style={st.btnDel} onClick={() => deletePlan(p.id)}>Delete</button>
          </div>
        ))}
      </div>
    )
  }

  // ---- EDIT VIEW ----
  return (
    <div className="fade-up">
      <div style={st.headRow}>
        <div>
          <div style={st.h2}>{form.id ? 'Edit' : 'New'} Passage Plan</div>
          <div style={st.sub}>{config?.vessel_name}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={st.btn} onClick={() => { setView('list'); setForm(blankForm()) }}>Cancel</button>
          <button style={st.primary} onClick={savePlan} disabled={saving}>{saving ? 'Saving…' : 'Save Plan'}</button>
        </div>
      </div>

      <div id="pp-print">
      {/* 1. PASSAGE SETUP */}
      <Section title="1 · Passage Setup" accent>
        <div style={st.grid2}>
          <Field label="Departure Port"><input style={st.in} value={form.departure_port} onChange={e => set('departure_port', e.target.value)} placeholder="e.g. Palma de Mallorca" /></Field>
          <Field label="Arrival Port"><input style={st.in} value={form.arrival_port} onChange={e => set('arrival_port', e.target.value)} placeholder="e.g. Monaco" /></Field>
          <Field label="Departure Date"><DateField value={form.departure_date} onChange={v => set('departure_date', v)} style={st.in} /></Field>
          <Field label="Arrival Date"><DateField value={form.arrival_date} onChange={v => set('arrival_date', v)} style={st.in} /></Field>
          <Field label={'Departure Time (local)' + (depUtc ? '  ·  UTC ' + depUtc : '')}>
            <input style={st.in} type="time" value={form.departure_time_local} onChange={e => set('departure_time_local', e.target.value)} />
          </Field>
          <Field label={'Arrival Time (local)' + (arrUtc ? '  ·  UTC ' + arrUtc : '')}>
            <input style={st.in} type="time" value={form.arrival_time_local} onChange={e => set('arrival_time_local', e.target.value)} />
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={st.fieldLabel}>Watch Leaders {crew.length > 0 && <span style={st.hint}>(from crew list)</span>}</div>
          <div style={st.chips}>
            {crew.length === 0 && <span style={st.muted}>No crew loaded — add crew in the Crew page.</span>}
            {crew.map(name => (
              <button key={name}
                onClick={() => toggleWatchLeader(name)}
                style={{ ...st.chip, ...(form.watch_leaders.includes(name) ? st.chipOn : {}) }}>
                {name}
              </button>
            ))}
          </div>
        </div>
      </Section>

      {/* 2. AUTO-INTELLIGENCE (placeholders — wired over coming sessions) */}
      <Section title="2 · Auto-Generated Intelligence" accent>
        <div style={st.autoNote}>Hey Captain auto-populates these from your route, vessel data and live sources. More coming online progressively.</div>

        {/* DISTANCE / SPEED / PASSAGE TIME */}
        <div style={st.liveCard}>
          <div style={st.liveTitle}>Distance &amp; Speed</div>
          <div style={st.spdGrid}>
            <div style={st.spdStat}>
              <div style={st.spdVal}>{dist.total ? dist.total.toFixed(1) : '–'}</div>
              <div style={st.spdLbl}>Total distance (NM)</div>
            </div>
            <Field label="Planned speed (kn)">
              <input style={st.in} type="number" step="0.5" value={form.auto_data?.speed_kn || ''} onChange={e => setSpeed(e.target.value)} placeholder="e.g. 12" />
            </Field>
            <div style={st.spdStat}>
              <div style={st.spdVal}>{passageHours != null ? fmtHrs(passageHours) : '–'}</div>
              <div style={st.spdLbl}>Passage time</div>
            </div>
            <div style={st.spdStat}>
              <div style={st.spdVal}>{requiredSpeed != null ? requiredSpeed + ' kn' : '–'}</div>
              <div style={st.spdLbl}>Req. avg speed (for arrival)</div>
            </div>
          </div>
          {dist.legs.length > 0 && (
            <div style={st.legRow}>
              {dist.legs.map((nm, i) => <span key={i} style={st.legChip}>Leg {i+1}: {nm.toFixed(1)} NM</span>)}
            </div>
          )}
          <div style={st.autoSub}>Distance from your plotted route. Enter speed to compute ETAs &amp; passage time, or set arrival time to see required average speed.</div>
        </div>

        {/* TIDES (live) */}
        <div style={st.liveCard}>
          <div style={st.liveHead}>
            <span style={st.liveTitle}>Tides — departure &amp; arrival</span>
            <button className="no-print" style={st.liveBtn} onClick={fetchTidesUKC} disabled={tideLoading}>{tideLoading ? 'Fetching…' : 'Fetch tides'}</button>
          </div>
          {tideErr && <div style={st.liveErr}>{tideErr}</div>}
          {['departure', 'arrival'].map(side => {
            const t = form.auto_data?.tides?.[side]
            if (!t) return null
            return (
              <div key={side} style={st.tidePort}>
                <div style={st.tidePortName}>{side === 'departure' ? '⚓ ' : '⚓ '}{t.port}{t.tidalRangeM != null ? '  ·  range ' + t.tidalRangeM + ' m' : ''}{t.station ? '  ·  ' + t.station : ''}</div>
                {!t.available && <div style={st.liveErr}>No tide station data for this port.</div>}
                <div style={st.tideRow}>
                  {(t.extremes || []).map((e, i) => (
                    <span key={i} style={{ ...st.tideChip, ...(e.type === 'High' ? st.tideHigh : st.tideLow) }}>
                      {e.type === 'High' ? 'HW' : 'LW'} {e.time} ({e.heightM}m)
                    </span>
                  ))}
                </div>
              </div>
            )
          })}
          {!form.auto_data?.tides && <div style={st.autoSub}>Enter departure/arrival ports above, then Fetch tides.</div>}
        </div>

        {/* UKC (calculated) */}
        <div style={st.liveCard}>
          <div style={st.liveTitle}>Under-Keel Clearance (UKC)</div>
          <div style={st.ukcGrid}>
            <Field label={'Vessel draft (m)' + (!isNaN(autoDraft) ? '  ·  auto: ' + autoDraft : '')}><input style={st.in} type="number" step="0.1" value={ukc.draft || ''} onChange={e => setUkcField('draft', e.target.value)} placeholder={!isNaN(autoDraft) ? String(autoDraft) + ' (from particulars)' : 'e.g. 3.8'} /></Field>
            <Field label="Charted depth — shallowest (m)"><input style={st.in} type="number" step="0.1" value={ukc.charted_depth || ''} onChange={e => setUkcField('charted_depth', e.target.value)} placeholder="e.g. 5.0" /></Field>
            <Field label="Tide height (m)"><input style={st.in} type="number" step="0.1" value={ukc.tide_height || ''} onChange={e => setUkcField('tide_height', e.target.value)} placeholder={form.auto_data?.tides?.departure?.lowestM != null ? 'auto: ' + form.auto_data.tides.departure.lowestM : 'e.g. 0.5'} /></Field>
          </div>
          {ukcResult != null && (
            <div style={{ ...st.ukcResult, ...(ukcResult < 0.5 ? st.ukcBad : ukcResult < 1 ? st.ukcWarn : st.ukcGood) }}>
              UKC = {ukcResult} m {ukcResult < 0.5 ? '⚠ insufficient' : ukcResult < 1 ? '⚠ marginal' : '✓'}
            </div>
          )}
          <div style={st.autoSub}>UKC = charted depth + tide height − draft. Uses lowest fetched tide if tide height left blank.</div>
        </div>
        {/* ROUTE (import) */}
        <div style={st.liveCard}>
          <div style={st.liveHead}>
            <span style={st.liveTitle}>Route — waypoints</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="no-print" style={st.liveBtn} onClick={onStartFromPorts} disabled={startBusy}>{startBusy ? 'Locating…' : 'Start route from ports'}</button>
              <label className="no-print" style={{ ...st.liveBtn, cursor: 'pointer' }}>
                Import route (GPX/RTZ)
                <input type="file" accept=".gpx,.rtz,.xml" onChange={onImportRoute} style={{ display: 'none' }} />
              </label>
              <button className="no-print" style={st.liveBtn} onClick={onDepthProfile} disabled={depthBusy || routeWaypoints.length < 2}>{depthBusy ? 'Sounding…' : 'Depth profile'}</button>
              <button className="no-print" style={st.liveBtn} onClick={onNavWarnings} disabled={warnBusy || routeWaypoints.length < 2}>{warnBusy ? 'Checking…' : 'Nav warnings'}</button>
              <button className="no-print" style={st.liveBtn} onClick={onWhaleCheck} disabled={whaleBusy || routeWaypoints.length < 2}>{whaleBusy ? 'Checking…' : 'Whale check'}</button>
              <button className="no-print" style={st.liveBtn} onClick={onPortInfo} disabled={portBusy}>{portBusy ? 'Looking up…' : 'Port info'}</button>
            </div>
          </div>
          <div style={st.autoSub}>Plot the route in your ECDIS and export it as RTZ or GPX. Import it here, then Fetch weather to see it on the map and along the route.</div>
          {importMsg && <div style={{ ...st.autoSub, color: '#2a6a96', marginTop: 6 }}>{importMsg}</div>}
          {/* route map (lives in Route card now, independent of weather) */}
          {routeWaypoints.length >= 2 && (
            <div style={{ marginTop: 12 }}>
              <div style={st.autoSub}>Edit the route — click to add, drag handles to move, click a handle to remove. Endpoints are fixed as ports. Seamarks = aids-to-navigation awareness (OpenSeaMap), not charted hazards — verify on the ECDIS.</div>
              <RouteEditor waypoints={routeWaypoints} onChange={setRouteWaypoints} weather={form.auto_data?.weather || null} selectedTime={timeline[timeIdx]} nextTime={timeline[Math.min(timeIdx+1, timeline.length-1)]} frac={tlFrac} speedKn={speedKn} depEpoch={form.departure_date ? new Date(form.departure_date + 'T' + (form.departure_time_local || '00:00')).getTime() : null} cachedSeamarks={form.auto_data?.seamarks} onSeamarks={(els) => setForm(f => ({ ...f, auto_data: { ...f.auto_data, seamarks: els } }))} />
              {depthMsg && <div style={{ ...st.autoSub, color: '#2a6a96', marginTop: 6 }}>{depthMsg}</div>}
              {form.auto_data?.depth_profile && <DepthProfileChart data={form.auto_data.depth_profile} draft={parseFloat(ukc.draft) || autoDraft} waypoints={routeWaypoints} wpCum={routeDistances(routeWaypoints).cum} />}
              {warnMsg && <div style={{ ...st.autoSub, color: '#2a6a96', marginTop: 6 }}>{warnMsg}</div>}
              {clearMsg && <div style={{ ...st.autoSub, color: '#d4a05a', marginTop: 6 }}>{clearMsg}</div>}
              {genMsg && <div style={{ ...st.autoSub, color: '#5ad1c4', marginTop: 6 }}>{genMsg}</div>}
              {form.auto_data?.nav_warnings && <NavWarningsCard data={form.auto_data.nav_warnings} />}
              {form.auto_data?.whale_zones && <WhaleWarningCard data={form.auto_data.whale_zones} />}
              {portMsg && <div style={{ ...st.autoSub, color: '#5ad1c4', marginTop: 6 }}>{portMsg}</div>}
              {form.auto_data?.port_info && <PortInfoCard data={form.auto_data.port_info} onAddContact={onAddContact} />}
            </div>
          )}
        </div>
        {/* WEATHER (live) */}
        <div style={st.liveCard}>
          <div style={st.liveHead}>
            <span style={st.liveTitle}>Weather — departure &amp; arrival</span>
            <button className="no-print" style={st.liveBtn} onClick={fetchWeather} disabled={wxLoading}>{wxLoading ? 'Fetching…' : 'Fetch weather'}</button>
          </div>
          {wxErr && <div style={st.liveErr}>{wxErr}</div>}
          {(() => {
            const wx = form.auto_data?.weather
            if (!wx) return <div style={st.autoSub}>Enter ports above, then Fetch weather for the along-route marine forecast.</div>
            // build the route sequence: departure -> waypoints -> arrival
            const seq = [wx.departure, ...(wx.waypoints || []), wx.arrival].filter(Boolean)
            // worst sea state along the route (the planning headline)
            const seas = seq.map(p => p.etaHour ? p.etaHour.waveM : p.current?.waveM).filter(v => v != null)
            const winds = seq.map(p => p.etaHour ? p.etaHour.windKn : p.current?.windKn).filter(v => v != null)
            const maxSea = seas.length ? Math.max(...seas) : null
            const maxWind = winds.length ? Math.max(...winds) : null
            return (
              <>
                {(maxSea != null || maxWind != null) && (
                  <div style={st.wxHeadline}>
                    Along-route max: <span style={{ color: wxWindCol(maxWind), fontWeight: 600 }}>{maxWind != null ? Math.round(maxWind) + ' kn wind' : ''}</span>
                    {maxSea != null && <> · <span style={{ color: wxWaveCol(maxSea), fontWeight: 600 }}>{maxSea} m sea</span></>}
                  </div>
                )}
                {seq.map((w, idx) => { try {
                  const c = w.current || {}
                  const isWP = (w.label || '').startsWith('Waypoint')
                  const ef = w.etaForecast
                  const fmtCoord = (v, pos, neg) => v == null ? '' : Math.abs(v).toFixed(3) + '° ' + (v >= 0 ? pos : neg)
                  const etaISO = (w.f != null) ? etaAt(w.f) : (w === wx.departure ? etaAt(0) : w === wx.arrival ? etaAt(1) : w.eta)
                  const eh = (() => {
                    if (!etaISO || !Array.isArray(w.hourly) || !w.hourly.length) return w.etaHour || null
                    const et = new Date(etaISO).getTime(); let best = null, bd = Infinity
                    for (const h of w.hourly) { const t = new Date(h.time).getTime(); const d = Math.abs(t - et); if (!isNaN(t) && d < bd) { best = h; bd = d } }
                    return best && bd <= 3 * 3600e3 ? best : null
                  })()
                  const etaStr = etaISO ? new Date(etaISO).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null
                  return (
                    <div key={idx} style={st.tidePort}>
                      <div style={st.wxPtHead}>
                        <span style={st.tidePortName}>{isWP ? '○ ' + w.label : '⚓ ' + (w.location || w.label)}</span>
                        {(w.lat != null && w.lon != null) && <span style={st.wxCoord}>{fmtCoord(w.lat,'N','S')}  {fmtCoord(w.lon,'E','W')}</span>}
                      </div>
                      {etaStr && <div style={st.wxEta}>ETA ≈ {etaStr}</div>}
                      {eh ? (
                        <>
                          <div style={st.wxLineLabel}>At ETA ({new Date(etaISO).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}):</div>
                          <div style={st.tideRow}>
                            <span style={{ ...st.wxChip, ...wxWindTone(eh.windKn) }}>Wind {eh.windKn != null ? Math.round(eh.windKn) : '–'} kn {eh.windCompass || ''}</span>
                            <span style={{ ...st.wxChip, ...wxWindTone(eh.gustKn) }}>Gust {eh.gustKn != null ? Math.round(eh.gustKn) : '–'} kn</span>
                            <span style={{ ...st.wxChip, ...wxWaveTone(eh.waveM) }}>Sea {eh.waveM != null ? eh.waveM : '–'} m</span>
                            <span style={{ ...st.wxChip, ...wxWaveTone(eh.swellM) }}>Swell {eh.swellM != null ? eh.swellM : '–'} m</span>
                            {eh.conditions && <span style={st.wxChipPlain}>{eh.conditions}</span>}
                          </div>
                        </>
                      ) : ef ? (
                        <>
                          <div style={st.wxLineLabel}>Forecast for passage day ({new Date(ef.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}):</div>
                          <div style={st.tideRow}>
                            <span style={{ ...st.wxChip, ...wxWindTone(ef.windMaxKn) }}>Wind {ef.windMaxKn != null ? Math.round(ef.windMaxKn) : '–'} kn {ef.windCompass || ''}</span>
                            <span style={{ ...st.wxChip, ...wxWindTone(ef.gustMaxKn) }}>Gust {ef.gustMaxKn != null ? Math.round(ef.gustMaxKn) : '–'} kn</span>
                            <span style={{ ...st.wxChip, ...wxWaveTone(ef.waveMaxM) }}>Sea {ef.waveMaxM != null ? ef.waveMaxM : '–'} m</span>
                            <span style={{ ...st.wxChip, ...wxWaveTone(ef.swellMaxM) }}>Swell {ef.swellMaxM != null ? ef.swellMaxM : '–'} m</span>
                            {ef.conditions && <span style={st.wxChipPlain}>{ef.conditions}</span>}
                          </div>
                        </>
                      ) : (w.eta && <div style={st.wxLineLabel}>No forecast for this ETA — date is outside the ~7-day forecast window (past date or too far ahead).</div>)}
                    </div>
                  )
                } catch (e) { console.warn('weather point skipped', e); return null }
                })}
                {/* Interactive Leaflet route editor: add / drag / remove waypoints */}
                {wx.route && routeWaypoints.length >= 2 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={st.liveHead}>
                      <span style={st.autoSub}>Forecast timeline — scrub to see conditions change along the route:</span>
                      <button className="no-print" style={st.liveBtn} onClick={updateRouteWeather} disabled={wxLoading}>{wxLoading ? 'Updating…' : 'Update weather for route'}</button>
                    </div>
                    {timeline.length > 0 && (
                      <div style={st.tlWrap}>
                        <div style={st.tlHead}>
                          <button className="no-print" style={st.tlPlay} onClick={() => setPlaying(p => !p)}>{playing ? '❚❚' : '▶'}</button>
                          <span style={st.tlTime}>{timeline[timeIdx] ? new Date(timeline[timeIdx]).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          <span style={st.tlHint}>scrub to see wind change along the route</span>
                        </div>
                        <input className="no-print" type="range" min={0} max={timeline.length - 1} value={timeIdx} onChange={e => { setPlaying(false); setTimeIdx(Number(e.target.value)) }} style={st.tlSlider} />
                        <div style={st.tlLegend}>
                          <span><span style={{ ...st.tlDot, background: '#137050' }} />&lt;17kn</span>
                          <span><span style={{ ...st.tlDot, background: '#8a5c0a' }} />17–22</span>
                          <span><span style={{ ...st.tlDot, background: '#d4a05a' }} />22–33</span>
                          <span><span style={{ ...st.tlDot, background: '#b4313c' }} />33+ gale</span>
                        </div>
                        {/* live readout per waypoint at the scrubbed time */}
                        <div style={st.tlReadout}>
                          {(() => {
                            const bp = boatHere()
                            if (!bp) return null
                            return (
                              <div style={st.tlHereRow} className="you-are-here">
                                <span style={st.tlHereName}>▶ You are here</span>
                                <span style={{ ...st.tlRowWind, color: tlWindCol(bp.windKn) }}>{bp.windKn != null ? Math.round(bp.windKn) + ' kn ' + (bp.windCompass || '') : '–'}</span>
                                <span style={st.tlRowSea}>{bp.gustKn != null ? 'G' + Math.round(bp.gustKn) : ''}</span>
                                <span style={st.tlRowSea}>{bp.waveM != null ? bp.waveM + ' m sea' : ''}</span>
                                <span style={st.tlRowSwell}>{bp.swellM != null ? bp.swellM + ' m swell' : ''}</span>
                              </div>
                            )
                          })()}
                          {[wx.departure, ...(wx.waypoints || []), wx.arrival].filter(Boolean).map((p, i) => {
                            const h = wxAtTime(p)
                            const isEnd = !((p.label || '').startsWith('Waypoint'))
                            return (
                              <div key={i} style={st.tlRow}>
                                <span style={st.tlRowName}>{isEnd ? (p.location || p.label) : p.label}</span>
                                <span style={{ ...st.tlRowWind, color: tlWindCol(h.windKn) }}>{h.windKn != null ? Math.round(h.windKn) + ' kn ' + (h.windCompass || '') : '–'}</span>
                                <span style={st.tlRowSea}>{h.gustKn != null ? 'G' + Math.round(h.gustKn) : ''}</span>
                                <span style={st.tlRowSea}>{h.waveM != null ? h.waveM + ' m sea' : ''}</span>
                                <span style={st.tlRowSwell}>{h.swellM != null ? h.swellM + ' m swell' : ''}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Windy map embed centred on the route */}
                {wx.route && (
                  <div className="no-print" style={{ marginTop: 12 }}>
                    <div style={st.autoSub}>Live wind &amp; wave map (Windy):</div>
                    <iframe
                      title="Windy passage map"
                      style={{ width: '100%', height: 320, border: '0.5px solid var(--border)', borderRadius: 10, marginTop: 6 }}
                      src={'https://embed.windy.com/embed2.html?lat=' + wx.route.midLat.toFixed(3) + '&lon=' + wx.route.midLon.toFixed(3) + '&detailLat=' + wx.route.midLat.toFixed(3) + '&detailLon=' + wx.route.midLon.toFixed(3) + '&width=650&height=320&zoom=7&level=surface&overlay=waves&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1'}
                    />
                    <div style={{ ...st.autoSub, textAlign: 'right', fontSize: 10.5 }}>{OSM_TEXT} · Weather map: Windy.com</div>
                    <div style={st.autoSub}>Waypoints are sampled along the direct route; forecast shown for each point's estimated passage day (daily resolution). Verify against official routing.</div>
                  </div>
                )}
              </>
            )
          })()}
        </div>
        <RegsCard
          depPort={form.departure_port}
          arrPort={form.arrival_port}
          vp={vp}
          route={form.auto_data?.weather?.route}
          saved={form.auto_data?.applicable_regs}
          onSave={(data) => setForm(f => ({ ...f, auto_data: { ...f.auto_data, applicable_regs: data } }))}
        />
      </Section>

      {/* 3. BRIDGE CHECKLIST (human attestations) */}
      <Section title="3 · Charts & Publications">
        {CHARTS_ITEMS.map(([k, label]) => <YesNo key={k} label={label} value={form.charts_publications[k]} onChange={v => setSection('charts_publications', k, v)} />)}
      </Section>
      <Section title="3 · Tidal Information">
        {TIDAL_ITEMS.map(([k, label]) => <YesNo key={k} label={label} value={form.tidal_info[k]} onChange={v => setSection('tidal_info', k, v)} />)}
      </Section>
      <Section title="3 · Weather & Marine Safety">
        {WEATHER_ITEMS.map(([k, label]) => <YesNo key={k} label={label} value={form.weather_msi[k]} onChange={v => setSection('weather_msi', k, v)} />)}
      </Section>
      <Section title="3 · Routing & Reporting">
        {ROUTING_ITEMS.map(([k, label]) => <YesNo key={k} label={label} value={form.routing_reporting[k]} onChange={v => setSection('routing_reporting', k, v)} />)}
      </Section>
      <Section title="3 · Bridge Equipment">
        {BRIDGE_ITEMS.map(([k, label]) => <YesNo key={k} label={label} value={form.bridge_equipment[k]} onChange={v => setSection('bridge_equipment', k, v)} />)}
      </Section>

      {/* 4. NOTES + SIGN-OFF (signatures + PDF come next session) */}
      <Section title="4 · Additional Notes">
        <textarea style={st.ta} rows={4} value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} placeholder="Any additional passage notes…" />
      </Section>
      <Section title="4 · Before Departure, Fuel & Watch Bill">
        <PreDepartureSection form={form} setForm={setForm} clientId={config?.id} />
        <FuelPlanSection form={form} setForm={setForm} distNm={dist.total} plannedSpeed={speedKn} />
        <WatchBillSection form={form} setForm={setForm} crew={crew} />
      </Section>

      <Section title="5 · Standing Orders & Watch Guidelines">
        <StandingOrdersSection form={form} setForm={setForm} clientId={config?.id} />
      </Section>

      <Section title="6 · Sign-off">
        <div style={st.autoNote}>Each signer draws their signature below. "Sign & Export PDF" saves a signed copy and archives it.</div>
        <div style={st.sigGrid}>
          {signers.map(sgn => (
            <div key={sgn.key} style={st.sigCell}>
              <div style={st.sigRole}>{sgn.role}</div>
              <div style={{ position: 'relative' }}>
                <canvas ref={sigCanvas(sgn.key)} id={sgn.key} style={st.sigPad} />
                <button className="no-print" onClick={() => clearCanvas(sgn.key)} style={st.sigClear}>Clear</button>
              </div>
              <div style={st.sigDate}>Date: {todayISO()}</div>
            </div>
          ))}
        </div>
      </Section>

      </div>{/* end pp-print */}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '16px 0 40px', flexWrap: 'wrap' }} className="no-print">
        <button style={st.btn} onClick={() => { setView('list'); setForm(blankForm()) }}>Cancel</button>
        <button style={st.primary} onClick={savePlan} disabled={saving}>{saving ? 'Saving…' : 'Save Plan'}</button>
        <button style={st.signBtn} onClick={signAndExport} disabled={exporting}>{exporting ? 'Exporting…' : 'Sign & Export PDF'}</button>
      </div>
    </div>
  )
}

// ============================================================
// LEAFLET ROUTE MAP (bundled Leaflet, src/lib/leaflet.js)
// ============================================================

function RouteEditor({ waypoints, onChange, weather, selectedTime, nextTime, frac, speedKn, depEpoch, cachedSeamarks, onSeamarks }) {
  // route: {lat1,lon1,lat2,lon2,...}  waypoints: ordered [{lat,lon,name,locked}]
  const ref = useRef(null)
  const mapRef = useRef(null)
  const layerRef = useRef(null)
  const seamarkLayerRef = useRef(null)
  const seamarkDataRef = useRef([])
  const wpRef = useRef(waypoints)
  wpRef.current = waypoints
  const wxRef = useRef({ weather, selectedTime, nextTime, frac, speedKn, depEpoch })
  wxRef.current = { weather, selectedTime, nextTime, frac, speedKn, depEpoch }

  // wind (kn) -> marker colour
  function windColor(kn) {
    if (kn == null) return '#2a6a96'
    if (kn >= 33) return '#b4313c'
    if (kn >= 22) return '#d4a05a'
    if (kn >= 17) return '#8a5c0a'
    return '#137050'
  }
  // find this point's wind {kn, dir} at the selected time (nearest-match by position)
  function windAt(lat, lon) {
    const wx = wxRef.current.weather
    if (!wx) return null
    const seq = [wx.departure, ...(wx.waypoints || []), wx.arrival].filter(Boolean)
    if (!seq.length) return null
    // nearest point by distance (tolerant of drag rounding)
    let pt = null, best = Infinity
    seq.forEach(p => {
      if (p.lat == null || p.lon == null) return
      const d = Math.hypot(p.lat - lat, p.lon - lon)
      if (d < best) { best = d; pt = p }
    })
    if (!pt || best > 0.5) return null
    const t = wxRef.current.selectedTime
    if (t && pt.hourly) { const h = pt.hourly.find(x => x.time === t); if (h) return { kn: h.windKn, dir: h.windCompass } }
    return { kn: pt.current?.windKn ?? null, dir: pt.current?.windCompass }
  }

  // ---- vessel position: where the boat is at the scrubbed time, given speed ----
  // returns { lat, lon } interpolated along the track, or null
  function boatPos() {
    const r = wxRef.current
    const spd = r.speedKn, dep = r.depEpoch, t = r.selectedTime
    if (!spd || !dep || !t) return null
    const wps = wpRef.current
    if (wps.length < 2) return null
    // smooth: interpolate effective time between this step and the next by frac
    const t0 = new Date(t).getTime()
    const t1 = r.nextTime ? new Date(r.nextTime).getTime() : t0
    const tEff = t0 + (t1 - t0) * (r.frac || 0)
    const hrs = (tEff - dep) / 3600000
    if (hrs <= 0) return { lat: wps[0].lat, lon: wps[0].lon, before: true }
    const nm = spd * hrs
    const rd = routeDistances(wps)
    if (nm >= rd.total) { const e = wps[wps.length-1]; return { lat: e.lat, lon: e.lon, after: true } }
    let leg = 1
    while (leg < rd.cum.length && rd.cum[leg] < nm) leg++
    const segStart = rd.cum[leg-1], segLen = rd.legs[leg-1] || 1e-9
    const f = (nm - segStart) / segLen
    const a = wps[leg-1], b = wps[leg]
    return { lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + (b.lon - a.lon) * f }
  }
  function boatIcon(L) {
    const svg = '<svg width="30" height="30" viewBox="0 0 30 30" style="display:block;">'
      + '<circle cx="15" cy="15" r="13" fill="rgba(106,170,212,.25)" stroke="#2a6a96" stroke-width="1.5"/>'
      + '<path d="M15 5 L21 22 L15 18 L9 22 Z" fill="#fff" stroke="#2a6a96" stroke-width="1"/></svg>'
    return L.divIcon({ className: 'pp-boat', html: svg, iconSize: [30,30], iconAnchor: [15,15] })
  }

  // distance from point P to segment AB (in degrees, good enough for ordering)
  function segDist(p, a, b) {
    const [px, py] = p, [ax, ay] = a, [bx, by] = b
    const dx = bx - ax, dy = by - ay
    const len2 = dx*dx + dy*dy || 1e-9
    let t = ((px - ax) * dx + (py - ay) * dy) / len2
    t = Math.max(0, Math.min(1, t))
    const cx = ax + t * dx, cy = ay + t * dy
    return Math.hypot(px - cx, py - cy)
  }

  function redraw(L, map) {
    if (layerRef.current) { layerRef.current.forEach(l => map.removeLayer(l)); }
    layerRef.current = []
    const wps = wpRef.current
    const pts = wps.map(w => [w.lat, w.lon])
    // line
    const line = L.polyline(pts, { color: '#8a5c0a', weight: 3, opacity: 0.9, dashArray: '6 6' }).addTo(map)
    layerRef.current.push(line)
    // markers
    wps.forEach((w, i) => {
      const wind = windAt(w.lat, w.lon)
      const kn = wind ? wind.kn : null
      const col = windColor(kn)
      const marker = L.circleMarker([w.lat, w.lon], {
        radius: w.locked ? 8 : 7,
        color: col, fillColor: col, fillOpacity: 0.9, weight: 2,
      }).addTo(map)
      marker.bindTooltip((w.name || ('WP' + i)) + (kn != null ? ' · ' + Math.round(kn) + ' kn ' + (wind.dir || '') : ''), { direction: 'top' })
      layerRef.current.push(marker)
    })
    // vessel position marker (moves with play/scrub)
    const bp = boatPos()
    if (bp) {
      const boat = L.marker([bp.lat, bp.lon], { icon: boatIcon(L), interactive: false, zIndexOffset: 1000 }).addTo(map)
      layerRef.current.push(boat)
    }
    // draggable handles. Endpoints (locked) are draggable too, but can't be removed by click.
    wps.forEach((w, i) => {
      const isEnd = w.locked
      const hw = windAt(w.lat, w.lon)
      const hcol = windColor(hw ? hw.kn : null)
      // endpoints get a distinct square white handle so they read as start/end
      const html = isEnd
        ? '<div style="width:18px;height:18px;border-radius:4px;background:#2c6fb3;border:2px solid #fff;cursor:move;box-shadow:0 0 5px rgba(0,0,0,.6);"></div>'
        : '<div style="width:16px;height:16px;border-radius:50%;background:' + hcol + ';border:2px solid #fff;cursor:move;box-shadow:0 0 4px rgba(0,0,0,.5);"></div>'
      const sz = isEnd ? 18 : 16
      const handle = L.marker([w.lat, w.lon], {
        draggable: true,
        icon: L.divIcon({ className: 'pp-wp-handle', html, iconSize: [sz,sz], iconAnchor: [sz/2,sz/2] }),
      }).addTo(map)
      handle.on('dragend', e => {
        const ll = e.target.getLatLng()
        const next = wpRef.current.slice()
        next[i] = { ...next[i], lat: ll.lat, lon: ll.lng }
        onChange(next)
      })
      if (!isEnd) {
        handle.on('click', () => {
          const next = wpRef.current.filter((_, idx) => idx !== i)
          onChange(next)
        })
      }
      layerRef.current.push(handle)
    })
  }

  useEffect(() => {
    let cancelled = false
    loadLeaflet().then(L => {
      if (cancelled || !ref.current) return
      if (!mapRef.current) {
        const map = L.map(ref.current, { scrollWheelZoom: false })
        // scrollWheelZoom enabled on click, disabled on mouse-out (so page scroll isn't hijacked)
        map.on('mouseover', () => map.scrollWheelZoom.enable())
        map.on('mouseout', () => map.scrollWheelZoom.disable())
        mapRef.current = map
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: OSM_ATTRIBUTION, maxZoom: 18 }).addTo(map)
        // OpenSeaMap seamark overlay (transparent: buoys, beacons, lights, harbours). Free, CC-BY. Aids-to-nav awareness, NOT charted hazards.
        const seamarks = L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', { attribution: 'Seamarks: OpenSeaMap (CC-BY-SA)', maxZoom: 18, opacity: 1 })
        seamarks.addTo(map)
        let seamarksOn = true
        const SeamarkToggle = L.Control.extend({
          options: { position: 'topright' },
          onAdd: function () {
            const btn = L.DomUtil.create('div')
            btn.style.cssText = 'background:rgba(15,20,28,.9);color:#cdd6e0;border:1px solid rgba(106,170,212,.4);border-radius:6px;padding:5px 9px;font:600 11px system-ui;cursor:pointer;user-select:none;box-shadow:0 1px 4px rgba(0,0,0,.4)'
            btn.innerHTML = '✓ Seamarks'
            L.DomEvent.disableClickPropagation(btn)
            btn.onclick = function () {
              seamarksOn = !seamarksOn
              if (seamarksOn) { seamarks.addTo(map); btn.innerHTML = '✓ Seamarks'; btn.style.opacity = '1' }
              else { map.removeLayer(seamarks); btn.innerHTML = 'Seamarks'; btn.style.opacity = '0.55' }
            }
            return btn
          },
        })
        map.addControl(new SeamarkToggle())
        // EMODnet bathymetry depth shading (WMS raster). Default OFF (tints the whole map).
        // GEBCO = global bathymetry base (works worldwide, coarser). EMODnet = finer detail over Europe.
        const gebco = L.tileLayer.wms('https://wms.gebco.net/mapserv', {
          layers: 'GEBCO_LATEST', format: 'image/png', transparent: true, opacity: 0.6,
          attribution: 'Bathymetry: GEBCO', maxZoom: 18,
        })
        const emodnet = L.tileLayer.wms('https://ows.emodnet-bathymetry.eu/wms', {
          layers: 'emodnet:mean_atlas_land', format: 'image/png', transparent: true, opacity: 0.55,
          attribution: 'Bathymetry: EMODnet', maxZoom: 18,
        })
        const depthGroup = L.layerGroup([gebco, emodnet])
        const depth = depthGroup
        let depthOn = false
        const DepthToggle = L.Control.extend({
          options: { position: 'topright' },
          onAdd: function () {
            const btn = L.DomUtil.create('div')
            btn.style.cssText = 'background:rgba(15,20,28,.9);color:#cdd6e0;border:1px solid rgba(90,209,196,.4);border-radius:6px;padding:5px 9px;font:600 11px system-ui;cursor:pointer;user-select:none;box-shadow:0 1px 4px rgba(0,0,0,.4);margin-top:4px;opacity:0.55'
            btn.innerHTML = 'Depth'
            L.DomEvent.disableClickPropagation(btn)
            btn.onclick = function () {
              depthOn = !depthOn
              if (depthOn) { depth.addTo(map); btn.innerHTML = '✓ Depth'; btn.style.opacity = '1' }
              else { map.removeLayer(depth); btn.innerHTML = 'Depth'; btn.style.opacity = '0.55' }
            }
            return btn
          },
        })
        map.addControl(new DepthToggle())
        // EMODnet Seabed Habitats — seagrass/Posidonia overlay (EU Habitats Directive protected).
        // Same upstream data behind Donia. Free, official, Mediterranean-wide. Default OFF.
        const seagrass = L.tileLayer.wms('https://ows.emodnet-seabedhabitats.eu/geoserver/emodnet_view/wms', {
          layers: 'seagrass_eov_poly_2025', format: 'image/png', transparent: true, opacity: 0.7,
          attribution: 'Seagrass: EMODnet Seabed Habitats', maxZoom: 18,
        })
        let seagrassOn = false
        const PosidoniaToggle = L.Control.extend({
          options: { position: 'topright' },
          onAdd: function () {
            const btn = L.DomUtil.create('div')
            btn.style.cssText = 'background:rgba(15,20,28,.9);color:#cdd6e0;border:1px solid rgba(90,209,196,.4);border-radius:6px;padding:5px 9px;font:600 11px system-ui;cursor:pointer;user-select:none;box-shadow:0 1px 4px rgba(0,0,0,.4);margin-top:4px;opacity:0.55'
            btn.innerHTML = 'Anchor (grass)'
            L.DomEvent.disableClickPropagation(btn)
            btn.onclick = function () {
              seagrassOn = !seagrassOn
              let lg = document.getElementById('seabed-legend')
              if (seagrassOn) {
                seagrass.addTo(map); btn.innerHTML = '✓ Anchor (grass)'; btn.style.opacity = '1'
                if (!lg) {
                  lg = L.DomUtil.create('div', '', map.getContainer())
                  lg.id = 'seabed-legend'
                  lg.style.cssText = 'position:absolute;bottom:24px;left:8px;z-index:1000;background:rgba(15,20,28,.92);color:#cdd6e0;border:1px solid rgba(90,209,196,.4);border-radius:6px;padding:7px 10px;font:11px system-ui;box-shadow:0 1px 4px rgba(0,0,0,.5);max-width:230px;line-height:1.5'
                  lg.innerHTML = '<div style="font-weight:700;margin-bottom:3px">Anchoring — seagrass</div><div><span style="display:inline-block;width:11px;height:11px;background:#3a9d3a;border-radius:2px;vertical-align:middle;margin-right:5px"></span><b>Green = protected grass — do NOT anchor</b></div><div style="margin-top:2px"><span style="display:inline-block;width:11px;height:11px;background:transparent;border:1px solid #9fb0c0;border-radius:2px;vertical-align:middle;margin-right:5px"></span>Clear = OK to drop (sand)</div><div style="color:#9fb0c0;margin-top:4px;font-size:10px">Reliable in the Mediterranean (surveyed). Posidonia is legally protected — fines + poor holding. No green ≠ guaranteed sand; verify on ECDIS / depth sounder. Source: EMODnet.</div>'
                }
              } else {
                map.removeLayer(seagrass); btn.innerHTML = 'Anchor (grass)'; btn.style.opacity = '0.55'
                if (lg) lg.remove()
              }
            }
            return btn
          },
        })
        map.addControl(new PosidoniaToggle())
        // NOAA right whale Seasonal Management Areas (US East Coast 10kn speed zones). Default OFF.
        let whaleLayer = null
        let whaleOn = false
        const loadWhaleZones = async () => {
          if (whaleLayer) { whaleLayer.addTo(map); return }
          try {
            const r = await fetch('https://coast.noaa.gov/arcgis/rest/services/OceanReports/NorthAtlanticRightWhaleSeasonalManagementAreas/MapServer/0/query?where=1%3D1&outFields=*&f=geojson&returnGeometry=true')
            const gj = await r.json()
            whaleLayer = L.geoJSON(gj, {
              style: { color: '#d4a05a', weight: 1.5, fillColor: '#d4a05a', fillOpacity: 0.25 },
              onEachFeature: (feat, layer) => {
                const p = feat.properties || {}
                layer.bindPopup('<b>Right whale speed zone</b><br>' + (p.location || '') + '<br>10 kn max — ' + (p.restrictedPeriod || '') + '<br><span style="font-size:10px;color:#888">Vessels ≥65 ft mandatory. ' + (p.CFRSection || '') + '</span>')
              },
            }).addTo(map)
          } catch (e) { console.log('[whale zones] load failed', e.message) }
        }
        const WhaleToggle = L.Control.extend({
          options: { position: 'topright' },
          onAdd: function () {
            const btn = L.DomUtil.create('div')
            btn.style.cssText = 'background:rgba(15,20,28,.9);color:#cdd6e0;border:1px solid rgba(212,160,90,.5);border-radius:6px;padding:5px 9px;font:600 11px system-ui;cursor:pointer;user-select:none;box-shadow:0 1px 4px rgba(0,0,0,.4);margin-top:4px;opacity:0.55'
            btn.innerHTML = 'Whale zones'
            L.DomEvent.disableClickPropagation(btn)
            btn.onclick = function () {
              whaleOn = !whaleOn
              let lg = document.getElementById('whale-legend')
              if (whaleOn) {
                loadWhaleZones(); btn.innerHTML = '✓ Whale zones'; btn.style.opacity = '1'
                if (!lg) {
                  lg = L.DomUtil.create('div', '', map.getContainer())
                  lg.id = 'whale-legend'
                  lg.style.cssText = 'position:absolute;bottom:24px;right:8px;z-index:1000;background:rgba(15,20,28,.92);color:#cdd6e0;border:1px solid rgba(212,160,90,.5);border-radius:6px;padding:7px 10px;font:11px system-ui;box-shadow:0 1px 4px rgba(0,0,0,.5);max-width:230px;line-height:1.5'
                  lg.innerHTML = '<div style="font-weight:700;margin-bottom:3px">Right whale speed zones</div><div style="color:#d4a05a">10 kn max (vessels ≥65 ft, seasonal)</div><div style="color:#9fb0c0;margin-top:4px;font-size:10px">NOAA Seasonal Management Areas, US East Coast. Mandatory in season. Source: NOAA Fisheries.</div>'
                }
              } else {
                if (whaleLayer) map.removeLayer(whaleLayer); btn.innerHTML = 'Whale zones'; btn.style.opacity = '0.55'
                if (lg) lg.remove()
              }
            }
            return btn
          },
        })
        map.addControl(new WhaleToggle())
        // click empty map -> insert waypoint at nearest segment
        map.on('click', e => {
          const p = [e.latlng.lat, e.latlng.lng]
          const wps = wpRef.current
          if (wps.length < 2) return
          let best = 1, bestD = Infinity
          for (let i = 0; i < wps.length - 1; i++) {
            const d = segDist(p, [wps[i].lat, wps[i].lon], [wps[i+1].lat, wps[i+1].lon])
            if (d < bestD) { bestD = d; best = i + 1 }
          }
          const next = wps.slice()
          next.splice(best, 0, { lat: p[0], lon: p[1], name: 'WP', locked: false })
          onChange(next)
        })
        const pts = wpRef.current.map(w => [w.lat, w.lon])
        if (pts.length >= 2) map.fitBounds(pts, { padding: [30, 30] })
        setTimeout(() => map.invalidateSize(), 100)
      }
      redraw(L, mapRef.current)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [waypoints, selectedTime, nextTime, frac, weather])

  // ---- clickable seamark detail (OpenSeaMap vector via Overpass) ----
  // builds a popup string from OSM seamark:* tags
  function seamarkPopup(tags) {
    const t = tags || {}
    const type = t['seamark:type'] || 'seamark'
    const name = t['seamark:name'] || t.name || ''
    const lines = []
    lines.push('<b>' + (name || type) + '</b>')
    lines.push('<span style="color:#2a6a96">' + type.replace(/_/g, ' ') + '</span>')
    // light characteristics (OSM uses numbered tags seamark:light:1:* and sometimes un-numbered)
    const g = k => t['seamark:light:' + k] || t['seamark:light:1:' + k]
    const lc = g('character'), lcol = g('colour')
    const per = g('period'), rng = g('range'), hgt = g('height')
    if (lc || lcol || per) {
      let lt = 'Light: ' + [lc, lcol].filter(Boolean).join(' ')
      if (per) lt += ' ' + per + 's'
      if (hgt) lt += ' ' + hgt + 'm'
      if (rng) lt += ' ' + rng + 'M'
      lines.push(lt)
    }
    // buoy/beacon shape + colour
    const shape = t['seamark:buoy_lateral:shape'] || t['seamark:beacon_lateral:shape']
    const bcol = t['seamark:buoy_lateral:colour'] || t['seamark:beacon_lateral:colour']
    if (shape || bcol) lines.push([bcol, shape].filter(Boolean).join(' '))
    lines.push('<i style="color:#8a94a0;font-size:10px">OpenSeaMap (community) — verify on ECDIS</i>')
    return lines.join('<br>')
  }

  // popup for a marina / harbour (community OSM tags)
  function marinaPopup(t) {
    t = t || {}
    const name = t['seamark:name'] || t.name || 'Marina / Harbour'
    const lines = ['<b>' + name + '</b>', '<span style=\"color:#5ad1c4\">' + (t['seamark:type'] === 'harbour' ? 'harbour' : 'marina') + '</span>']
    const vhf = t['seamark:harbour:channel'] || t['vhf'] || t['communication:vhf']
    if (vhf) lines.push('VHF Ch ' + vhf)
    if (t.operator) lines.push('Operator: ' + t.operator)
    if (t.phone || t['contact:phone']) lines.push('Tel: ' + (t.phone || t['contact:phone']))
    if (t.website || t['contact:website']) lines.push('Web: ' + (t.website || t['contact:website']))
    if (t['capacity:berths'] || t.berths) lines.push('Berths: ' + (t['capacity:berths'] || t.berths))
    lines.push('<i style=\"color:#8a94a0;font-size:10px\">OpenSeaMap/OSM (community) — verify on ECDIS / pilot book</i>')
    return lines.join('<br>')
  }

  // fetch seamarks in the route bbox from Overpass, render as clickable markers.
  // own layer + own effect: only re-runs when the route (waypoints) changes, NOT on scrub.
  useEffect(() => {
    let cancelled = false
    const wps = waypoints
    if (!wps || wps.length < 2) return
    // seamark fetch (map-ready aware): wait until the map exists, then query
    loadLeaflet().then(L => {
      let tries = 0
      const run = () => {
        if (cancelled) return
        if (!mapRef.current) { if (tries++ < 40) setTimeout(run, 150); return }
        const map = mapRef.current
      const lats = wps.map(w => w.lat), lons = wps.map(w => w.lon)
      let minLat = Math.min(...lats), maxLat = Math.max(...lats)
      let minLon = Math.min(...lons), maxLon = Math.max(...lons)
      // margin, and guard against an enormous bbox (cap ~6 deg span)
      const mLat = 0.15, mLon = 0.15
      minLat -= mLat; maxLat += mLat; minLon -= mLon; maxLon += mLon
      if ((maxLat - minLat) > 6 || (maxLon - minLon) > 6) { console.log('[seamarks] bbox too big, skipped', (maxLat-minLat).toFixed(1), (maxLon-minLon).toFixed(1)); return }
      // render a helper to draw a list of marks (used by both cache + fresh fetch)
      const drawMarks = (els) => {
        if (seamarkLayerRef.current) { seamarkLayerRef.current.forEach(l => map.removeLayer(l)) }
        seamarkLayerRef.current = []
        seamarkDataRef.current = els
        els.forEach(e => {
          const t = e.tags || {}
          const isMarina = t.leisure === 'marina' || t['seamark:type'] === 'harbour'
          const base = isMarina ? 9 : 8
          const fill = isMarina ? '#5ad1c4' : '#ffd24a'
          const mk = L.circleMarker([e.lat, e.lon], { radius: base, color: '#1a1f28', fillColor: fill, fillOpacity: 0.95, weight: 2, bubblingMouseEvents: false })
          const html = isMarina ? marinaPopup(t) : seamarkPopup(t)
          mk.bindTooltip(html, { direction: 'top', opacity: 0.97, sticky: true })
          mk.bindPopup(html, { maxWidth: 260 })
          mk.on('mouseover', function () { this.setStyle({ radius: base + 3, weight: 3 }) })
          mk.on('mouseout', function () { this.setStyle({ radius: base, weight: 2 }) })
          mk.on('click', function (ev) { L.DomEvent.stopPropagation(ev); this.openPopup() })
          mk.addTo(map)
          seamarkLayerRef.current.push(mk)
        })
      }
      // if we have cached marks, draw them immediately (no network needed)
      if (cachedSeamarks && cachedSeamarks.length) {
        console.log('[seamarks] drawing', cachedSeamarks.length, 'from cache')
        drawMarks(cachedSeamarks)
        return
      }
      const q = '[out:json][timeout:15];('
        + 'way["leisure"="marina"](' + minLat.toFixed(3) + ',' + minLon.toFixed(3) + ',' + maxLat.toFixed(3) + ',' + maxLon.toFixed(3) + ');'
        + 'node["leisure"="marina"](' + minLat.toFixed(3) + ',' + minLon.toFixed(3) + ',' + maxLat.toFixed(3) + ',' + maxLon.toFixed(3) + ');'
        + 'way["seamark:type"="harbour"](' + minLat.toFixed(3) + ',' + minLon.toFixed(3) + ',' + maxLat.toFixed(3) + ',' + maxLon.toFixed(3) + ');'
        + 'node["seamark:type"="harbour"](' + minLat.toFixed(3) + ',' + minLon.toFixed(3) + ',' + maxLat.toFixed(3) + ',' + maxLon.toFixed(3) + ');'
        + 'node["seamark:type"](' + minLat.toFixed(3) + ',' + minLon.toFixed(3) + ',' + maxLat.toFixed(3) + ',' + maxLon.toFixed(3) + ');'
        + ');out center 300;'
      fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'overpass', query: q }) })
          .then(r => { if (!r.ok) throw new Error('status ' + r.status); return r.json() })
          .then(json => {
          if (cancelled || !mapRef.current) return
          // clear old vector seamarks
          if (seamarkLayerRef.current) { seamarkLayerRef.current.forEach(l => map.removeLayer(l)) }
          seamarkLayerRef.current = []
          const els = (json.elements || []).map(e => {
            const lat = e.lat != null ? e.lat : (e.center && e.center.lat)
            const lon = e.lon != null ? e.lon : (e.center && e.center.lon)
            return (lat != null && lon != null) ? { ...e, lat, lon } : null
          }).filter(Boolean)
          seamarkDataRef.current = els
          console.log('[seamarks] loaded', els.length, 'marks for bbox')
          if (typeof onSeamarks === 'function') onSeamarks(els)
          els.forEach(e => {
            const t = e.tags || {}
            const isMarina = t.leisure === 'marina' || t['seamark:type'] === 'harbour'
            const base = isMarina ? 9 : 8
            const fill = isMarina ? '#5ad1c4' : '#ffd24a'
            const mk = L.circleMarker([e.lat, e.lon], { radius: base, color: '#1a1f28', fillColor: fill, fillOpacity: 0.95, weight: 2, bubblingMouseEvents: false })
            const html = isMarina ? marinaPopup(t) : seamarkPopup(t)
            mk.bindTooltip(html, { direction: 'top', opacity: 0.97, sticky: true })
            mk.bindPopup(html, { maxWidth: 260 })
            mk.on('mouseover', function () { this.setStyle({ radius: base + 3, weight: 3 }) })
            mk.on('mouseout', function () { this.setStyle({ radius: base, weight: 2 }) })
            mk.on('click', function (ev) { L.DomEvent.stopPropagation(ev); this.openPopup() })
            mk.addTo(map)
            seamarkLayerRef.current.push(mk)
          })
        })
          .catch((err) => { console.log('[seamarks] proxy fetch failed:', err.message) })
      }
      run()
    }).catch(() => {})
    return () => { cancelled = true }
  }, [waypoints])

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null } }, [])

  return (
    <div>
      <div ref={ref} style={{ width: '100%', height: 360, borderRadius: 10, border: '0.5px solid var(--border)', marginTop: 6, overflow: 'hidden' }} />
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>Click the map to add a waypoint · drag the blue handles to move · click a handle to remove. Endpoints (ports) are fixed.</div>
    </div>
  )
}

// ============================================================
// SUB-COMPONENTS
// ============================================================
function Section({ title, children, accent }) {
  return (
    <div style={{ ...st.section, ...(accent ? st.sectionAccent : {}) }}>
      <div style={st.secTitle}>{title}</div>
      {children}
    </div>
  )
}
function Field({ label, children }) {
  return <div><div style={st.fieldLabel}>{label}</div>{children}</div>
}
function YesNo({ label, value, onChange }) {
  return (
    <div style={st.ynRow}>
      <span style={st.ynLabel}>{label}</span>
      <div style={st.ynBtns}>
        <button style={{ ...st.yn, ...(value === 'yes' ? st.ynYes : {}) }} onClick={() => onChange(value === 'yes' ? null : 'yes')}>Yes</button>
        <button style={{ ...st.yn, ...(value === 'no' ? st.ynNo : {}) }} onClick={() => onChange(value === 'no' ? null : 'no')}>No</button>
        <button style={{ ...st.yn, ...(value === 'na' ? st.ynNa : {}) }} onClick={() => onChange(value === 'na' ? null : 'na')}>N/A</button>
      </div>
    </div>
  )
}
function renderRegsMarkdown(text) {
  if (!text) return null
  const renderInline = (str) => str.split(/(\*\*[^*]+\*\*)/g).map((p, i) => /^\*\*[^*]+\*\*$/.test(p) ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>)
  const lines = String(text).split("\n")
  const out = []
  let listBuf = []
  const flush = (k) => { if (listBuf.length) { out.push(<ul key={"ul"+k} style={{ margin: "4px 0", paddingLeft: 18 }}>{listBuf.map((li, i) => <li key={i} style={{ marginBottom: 2 }}>{renderInline(li)}</li>)}</ul>); listBuf = [] } }
  lines.forEach((ln, idx) => {
    const t = ln.trim()
    if (/^[-*\u2022]\s+/.test(t)) { listBuf.push(t.replace(/^[-*\u2022]\s+/, "")); return }
    flush(idx)
    if (t === "") { out.push(<div key={"sp"+idx} style={{ height: 6 }} />); return }
    if (/^#{1,4}\s+/.test(t)) { out.push(<div key={idx} style={{ fontWeight: 700, margin: '8px 0 2px' }}>{renderInline(t.replace(/^#{1,4}\s+/, '').replace(/\*\*/g, ''))}</div>); return }
    out.push(<div key={idx} style={{ marginBottom: 4 }}>{renderInline(t)}</div>)
  })
  flush("end")
  return out
}

function RegsCard({ depPort, arrPort, vp, route, saved, onSave }) {
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const data = saved || null

  function vesselDescriptor() {
    if (!vp) return 'a yacht'
    const flag = vp['Flag'], loa = vp['LOA'], gt = vp['Gross Tonnage'], cls = vp['Class'], name = vp['Vessels Name']
    const parts = []
    if (flag) parts.push(flag + '-flagged')
    parts.push('motor yacht')
    if (loa) parts.push('LOA ' + loa)
    if (gt) parts.push(gt + ' gross tonnage')
    if (cls) parts.push('class ' + cls)
    let d = parts.join(', ')
    if (name) d = name + ' (' + d + ')'
    return d
  }
  function buildPrompt() {
    const v = vesselDescriptor()
    return 'Vessel: ' + v + '. '
      + 'This vessel is planning a passage from ' + (depPort || '?') + ' to ' + (arrPort || '?') + '. '
      + 'Give the SPECIFIC, ACTIONABLE regulatory requirements for THIS vessel on THIS route, not general descriptions. '
      + 'Take the flag state and gross tonnage into account, since thresholds (e.g. 500 GT, 3000 GT) change which rules apply; '
      + 'state explicitly when a requirement does or does NOT apply because of this tonnage or flag. '
      + 'For each applicable item, state the exact rule and the concrete figure or action: '
      + 'for emission/MARPOL areas the exact sulphur limit (percent m/m) and any NOx tier; '
      + 'for any Traffic Separation Scheme the specific named TSS and the rule for using or crossing it; '
      + 'for ship reporting systems the named system and what must be reported and when; '
      + 'for VTS the named service and reporting points/channels if known; '
      + 'for waste/sewage/garbage discharge the exact distances-from-land and conditions. '
      + 'Organise by topic with a short heading and 1-3 specific bullet points each. Cite the source document for every requirement. '
      + 'If the documents do not contain a specific figure, say not specified in available documents rather than giving generic advice; never invent numbers.'
  }

  async function generate() {
    setErr(''); setLoading(true)
    try {
      const r = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'ask', prompt: buildPrompt() }),
      })
      const d = await r.json()
      if (d.error) { setErr(d.error) }
      else { onSave({ answer: d.answer || '', sources: d.sources || [], generatedAt: new Date().toISOString(), route: depPort + ' to ' + arrPort }) }
    } catch (e) {
      setErr('Could not reach the Regulations assistant. Please try again.')
    } finally { setLoading(false) }
  }

  const canGen = depPort && arrPort
  return (
    <div style={st.regsCard}>
      <div style={st.regsHead}>
        <div>
          <div style={st.regsTitle}>Applicable regulations</div>
          <div style={st.autoSub}>Route-specific compliance, grounded in the Regulations library.</div>
        </div>
        <button className="no-print" style={{ ...st.liveBtn, opacity: canGen ? 1 : 0.5 }} onClick={generate} disabled={loading || !canGen}>
          {loading ? 'Checking…' : (data ? 'Regenerate' : 'Generate')}
        </button>
      </div>
      {!canGen && <div style={st.autoSub}>Enter departure &amp; arrival ports above first.</div>}
      {err && <div style={st.regsErr}>{err}</div>}
      {data && (
        <div style={st.regsBody}>
          <div style={st.regsAnswer}>{renderRegsMarkdown(data.answer)}</div>
          {data.sources && data.sources.length > 0 && (
            <div style={st.regsChips}>
              {data.sources.map((s, i) => (
                <span key={i} style={st.regsChip}>{typeof s === 'string' ? s : (s.title || s.file_name || s.name || 'source')}</span>
              ))}
            </div>
          )}
          <div style={st.regsDisc}>AI-assisted guidance — always verify against the linked source document. Not a substitute for official advice.</div>
        </div>
      )}
    </div>
  )
}

function DepthProfileChart({ data, draft, waypoints, wpCum }) {
  const pts = (data && data.points || []).filter(p => p.depth != null)
  const [depthCap, setDepthCap] = useState(150) // 'full' or a number (m)
  if (pts.length < 2) return <div style={st.autoSub}>No depth data along this route.</div>
  const W = 700, H = 200, padL = 44, padR = 10, padT = 12, padB = 26
  const maxNm = data.total || pts[pts.length-1].nm || 1
  const dataMax = Math.max(...pts.map(p => p.depth), 10)
  const maxDepth = (depthCap === 'full') ? dataMax : Math.min(depthCap, dataMax)
  const x = nm => padL + (nm / maxNm) * (W - padL - padR)
  const y = d => padT + (Math.min(Math.max(0, d), maxDepth) / maxDepth) * (H - padT - padB)
  // seabed polygon (depth line down to bottom)
  let path = 'M ' + x(pts[0].nm) + ' ' + y(pts[0].depth)
  pts.forEach(p => { path += ' L ' + x(p.nm) + ' ' + y(p.depth) })
  const area = path + ' L ' + x(pts[pts.length-1].nm) + ' ' + (H - padB) + ' L ' + x(pts[0].nm) + ' ' + (H - padB) + ' Z'
  // shallow flag threshold: draft + 5m safety, min 10m
  const shallowTh = Math.max((draft || 3) + 5, 10)
  const shallow = pts.filter(p => p.depth <= shallowTh)
  const minDepth = Math.min(...pts.map(p => p.depth))
  // y gridlines
  const gridDepths = [0, Math.round(maxDepth*0.25), Math.round(maxDepth*0.5), Math.round(maxDepth*0.75), Math.round(maxDepth)]
  return (
    <div style={{ marginTop: 12 }}>
      <div style={st.autoSub}>Seabed depth along route ({data.source || 'GEBCO'} — situational awareness, verify soundings on the ECDIS). Shallowest: {Math.round(minDepth)} m.</div>
      <div className="no-print" style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {[50, 150, 500, 'full'].map(r => (
          <button key={r} onClick={() => setDepthCap(r)} style={{ ...st.liveBtn, padding: '3px 9px', fontSize: 11, opacity: depthCap === r ? 1 : 0.5, borderColor: depthCap === r ? '#2a6a96' : 'var(--border)' }}>{r === 'full' ? 'Full' : r + 'm'}</button>
        ))}
      </div>
      <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: 'auto', marginTop: 6, background: 'var(--deep)', borderRadius: 8, border: '0.5px solid var(--border)' }}>
        {gridDepths.map((gd, i) => (
          <g key={i}>
            <line x1={padL} y1={y(gd)} x2={W-padR} y2={y(gd)} stroke="rgba(255,255,255,.08)" strokeWidth="0.5" />
            <text x={padL-6} y={y(gd)+3} fill="var(--muted)" fontSize="9" textAnchor="end">{gd}m</text>
          </g>
        ))}
        <path d={area} fill="rgba(106,170,212,.25)" />
        <path d={path} fill="none" stroke="#2a6a96" strokeWidth="1.5" />
        {shallow.length > 0 && shallow.map((p, i) => (
          <circle key={i} cx={x(p.nm)} cy={y(p.depth)} r="3" fill={p.depth <= (draft||3)+2 ? '#b4313c' : '#d4a05a'} />
        ))}
        <text x={padL} y={H-8} fill="var(--muted)" fontSize="9">0 NM</text>
        <text x={W-padR} y={H-8} fill="var(--muted)" fontSize="9" textAnchor="end">{Math.round(maxNm)} NM</text>
        {(waypoints || []).map((w, i) => {
          const cum = (wpCum || [])[i]
          if (cum == null) return null
          const px = x(cum)
          const isEnd = (i === 0 || i === waypoints.length - 1)
          return (
            <g key={'wp' + i}>
              <line x1={px} y1={padT} x2={px} y2={H - padB} stroke={isEnd ? 'rgba(201,147,58,.6)' : 'rgba(255,255,255,.25)'} strokeWidth="0.75" strokeDasharray="3 3" />
              <text x={px} y={padT - 2} fill={isEnd ? '#8a5c0a' : 'var(--muted)'} fontSize="8" textAnchor="middle">{i === 0 ? 'D' : (i === waypoints.length - 1 ? 'A' : String(i))}</text>
            </g>
          )
        })}
      </svg>
      {shallow.length > 0 && <div style={{ ...st.autoSub, color: '#d4a05a', marginTop: 4 }}>⚠ {shallow.length} sample(s) shallower than {Math.round(shallowTh)} m — verify UKC and soundings on the chart.</div>}
    </div>
  )
}

function WhaleWarningCard({ data }) {
  const crossed = (data && data.crossed) || []
  return (
    <div style={{ marginTop: 12 }}>
      {crossed.length === 0 ? (
        <div style={{ background: 'var(--deep)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--muted)' }}>
          No right-whale speed zones on your route (NOAA Seasonal Management Areas, US East Coast).
        </div>
      ) : (
        <div>
          <div style={{ ...st.autoSub, color: '#d4a05a' }}>⚠ Route crosses NOAA right-whale speed zone(s). Vessels ≥65 ft must transit at 10 kn or less in season (mandatory, 50 CFR 224.105). Verify current dates — NOAA also issues dynamic/voluntary zones.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {crossed.map((z, i) => (
              <div key={i} style={{ background: 'var(--deep)', border: '0.5px solid rgba(212,160,90,.5)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#d4a05a' }}>{z.location}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>10 kn max</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text)', marginTop: 3 }}>Seasonal restriction: {z.restrictedPeriod}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{z.restriction} · {z.cfr}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function NavWarningsCard({ data }) {
  const items = (data && data.items) || []
  if (!items.length) return null
  const areaName = (a) => ({ '4': 'NAVAREA IV', '12': 'NAVAREA XII', 'A': 'HYDROLANT', 'P': 'HYDROPAC', 'C': 'HYDROARC' }[a] || a)
  return (
    <div style={{ marginTop: 12 }}>
      <div style={st.autoSub}>Active navigational warnings near your route (NGA MSI — worldwide). Supplement only; verify against official GMDSS/NAVTEX broadcasts.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {items.map((w, i) => (
          <div key={i} style={{ background: 'var(--deep)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#d4a05a' }}>{areaName(w.navArea)} {w.msgNumber}/{w.msgYear}</span>
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>~{w.distNm} NM</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text)', whiteSpace: 'pre-line', lineHeight: 1.4, fontFamily: 'ui-monospace, monospace' }}>{w.text.length > 320 ? w.text.slice(0, 320) + '…' : w.text}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ContactForm({ portKey, onAddContact }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState({ contact_name: '', role: '', phone: '', vhf: '', email: '', website: '', notes: '' })
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }))
  const inp = { background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 5, padding: '5px 7px', fontSize: 11, color: 'var(--text)', width: '100%', boxSizing: 'border-box' }
  if (!open) return (
    <button className="no-print" onClick={() => setOpen(true)} style={{ ...st.liveBtn, fontSize: 11, padding: '4px 10px', marginTop: 8 }}>+ Add contact</button>
  )
  return (
    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
      <input style={inp} placeholder="Name *" value={f.contact_name} onChange={set('contact_name')} />
      <input style={inp} placeholder="Role (agent, HM, fuel…)" value={f.role} onChange={set('role')} />
      <input style={inp} placeholder="Phone" value={f.phone} onChange={set('phone')} />
      <input style={inp} placeholder="VHF" value={f.vhf} onChange={set('vhf')} />
      <input style={inp} placeholder="Email" value={f.email} onChange={set('email')} />
      <input style={inp} placeholder="Website" value={f.website} onChange={set('website')} />
      <input style={{ ...inp, gridColumn: '1 / 3' }} placeholder="Notes" value={f.notes} onChange={set('notes')} />
      <div style={{ gridColumn: '1 / 3', display: 'flex', gap: 6 }}>
        <button className="no-print" disabled={busy || !f.contact_name.trim()} onClick={async () => { setBusy(true); const ok = await onAddContact(portKey, f); setBusy(false); if (ok) { setF({ contact_name: '', role: '', phone: '', vhf: '', email: '', website: '', notes: '' }); setOpen(false) } }} style={{ ...st.liveBtn, fontSize: 11, padding: '4px 12px', opacity: (busy || !f.contact_name.trim()) ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Save contact'}</button>
        <button className="no-print" onClick={() => setOpen(false)} style={{ ...st.liveBtn, fontSize: 11, padding: '4px 12px' }}>Cancel</button>
      </div>
    </div>
  )
}

function PortInfoCard({ data, onAddContact }) {
  const results = (data && data.results) || []
  const shown = results.filter(r => r.found && r.port)
  // also show ports that weren't in WPI but may have a portKey (so contacts still work)
  const extras = results.filter(r => !(r.found && r.port) && r.portKey)
  if (!shown.length && !extras.length) return null
  const SIZE = { V: 'Very small', S: 'Small', M: 'Medium', L: 'Large' }
  const SHELTER = { E: 'Excellent', G: 'Good', F: 'Fair', P: 'Poor', N: 'None' }
  const HARBOR = { CN: 'Coastal natural', CB: 'Coastal breakwater', CT: 'Coastal tide gate', LC: 'Lake/canal', RN: 'River natural', RB: 'River basin', RT: 'River tide gate', TN: 'Typhoon harbour', OR: 'Open roadstead' }
  const REPAIR = { A: 'Major', B: 'Moderate', C: 'Limited', D: 'Emergency only', N: 'None' }
  const DRY = { S: 'Small', M: 'Medium', L: 'Large' }
  const yn = (v) => v === 'Y' ? 'Yes' : (v === 'N' ? 'No' : null)
  const Row = ({ label, val }) => (val == null || val === '' || val === 'None') ? null : (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', borderBottom: '0.5px solid var(--border)' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ color: 'var(--text)', textAlign: 'right', maxWidth: '60%' }}>{val}</span>
    </div>
  )
  const pilot = (p) => p.ptCompulsory === 'Y' ? 'Compulsory' : (p.ptAvailable === 'Y' ? 'Available' : (p.ptAdvisable === 'Y' ? 'Advisable' : null))
  const supplies = (p) => [p.suProvisions === 'Y' && 'Provisions', p.suWater === 'Y' && 'Water', p.suFuel === 'Y' && 'Fuel oil', p.suDiesel === 'Y' && 'Diesel', p.suAviationFuel === 'Y' && 'Aviation fuel'].filter(Boolean).join(', ') || null
  const depths = (p) => [p.chDepth && ('Channel ' + p.chDepth + 'm'), p.anDepth && ('Anchorage ' + p.anDepth + 'm'), p.cpDepth && ('Pier ' + p.cpDepth + 'm')].filter(Boolean).join(' · ') || null
  const maxV = (p) => { const a = []; if (p.maxVesselLength) a.push('LOA ' + p.maxVesselLength + 'm'); if (p.maxVesselDraft) a.push('Draft ' + p.maxVesselDraft + 'm'); if (p.maxVesselBeam) a.push('Beam ' + p.maxVesselBeam + 'm'); return a.join(' · ') || null }
  const ypUrl = (name) => 'https://www.yachting-pages.com/listing/port-marina-dock-equipment/' + encodeURIComponent(String(name).toLowerCase().trim().replace(/\s+/g, '-'))
  const navilyUrl = (res) => { const p = res.port || {}; const lat = p.ycoord || res.lat; const lon = p.xcoord || res.lon; return (lat != null && lon != null) ? ('https://www.navily.com/map/#/13/' + Number(lat).toFixed(4) + '/' + Number(lon).toFixed(4)) : 'https://www.navily.com/' }

  const Contacts = ({ res }) => {
    const cs = res.contacts || []
    return (
      <div style={{ marginTop: 8, borderTop: '0.5px solid var(--border)', paddingTop: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#5ad1c4', marginBottom: 4 }}>SHARED CONTACTS</div>
        {cs.length === 0 && <div style={{ fontSize: 11, color: 'var(--muted)' }}>No saved contacts yet. Add one to help every crew.</div>}
        {cs.map((c, j) => (
          <div key={j} style={{ fontSize: 11, color: 'var(--text)', padding: '3px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontWeight: 600 }}>{c.contact_name}</span>{c.role ? ' · ' + c.role : ''}
            <div style={{ color: 'var(--muted)', fontSize: 10 }}>
              {[c.phone, c.vhf && ('VHF ' + c.vhf), c.email, c.website].filter(Boolean).join(' · ')}
              {c.notes ? (' — ' + c.notes) : ''}
            </div>
          </div>
        ))}
        <ContactForm portKey={res.portKey} onAddContact={onAddContact} />
        <a href={ypUrl(res.portKey)} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, fontSize: 10, color: '#2a6a96' }}>Search agents & services on Yachting Pages ↗</a>
        <a href={navilyUrl(res)} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, marginLeft: 10, fontSize: 10, color: '#2a6a96' }}>Anchorages & reviews on Navily ↗</a>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={st.autoSub}>Port info (NGA World Port Index, official) + shared crew contacts. Verify against current Sailing Directions & charts.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
        {shown.map((res, i) => {
          const p = res.port
          return (
            <div key={i} style={{ background: 'var(--deep)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{p.portName}</span>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>{p.countryName} · {p.unloCode || ''}</span>
              </div>
              <Row label="Harbour" val={[SIZE[p.harborSize], HARBOR[p.harborType], SHELTER[p.shelter] && (SHELTER[p.shelter] + ' shelter')].filter(Boolean).join(' · ') || null} />
              <Row label="Max vessel" val={maxV(p)} />
              <Row label="Depths" val={depths(p)} />
              <Row label="Pilotage" val={pilot(p)} />
              <Row label="Tugs" val={[p.tugsAssist === 'Y' && 'Assist', p.tugsSalvage === 'Y' && 'Salvage'].filter(Boolean).join(', ') || null} />
              <Row label="Supplies" val={supplies(p)} />
              <Row label="Repairs" val={REPAIR[p.repairCode]} />
              <Row label="Drydock" val={DRY[p.drydock]} />
              <Row label="Medical" val={yn(p.medFacilities)} />
              <Row label="Radio / VHF" val={yn(p.cmRadio) && 'Available'} />
              <Row label="ETA message" val={p.etaMessage === 'Y' ? 'Required' : null} />
              <Row label="TSS / VTS" val={[p.tss === 'Y' && 'TSS', p.vts === 'Y' && 'VTS'].filter(Boolean).join(' · ') || null} />
              <Row label="Reference" val={[p.publicationNumber, p.chartNumber && ('Chart ' + p.chartNumber)].filter(Boolean).join(' · ') || null} />
              <Contacts res={res} />
            </div>
          )
        })}
        {extras.map((res, i) => (
          <div key={'x'+i} style={{ background: 'var(--deep)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{res.portKey}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)' }}>Not in World Port Index — contacts only.</div>
            <Contacts res={res} />
          </div>
        ))}
      </div>
    </div>
  )
}

function AutoCard({ label, status, note }) {
  return (
    <div style={st.autoCard}>
      <div style={{ flex: 1 }}>
        <div style={st.autoLabel}>{label}</div>
        <div style={st.autoSub}>{note}</div>
      </div>
      <span style={st.autoBadge}>auto · soon</span>
    </div>
  )
}

// ============================================================
// STYLES
// ============================================================
const st = {
  muted: { color: 'var(--muted)', fontSize: 13, padding: 20 },
  headRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 12, flexWrap: 'wrap' },
  h2: { fontSize: 20, fontWeight: 600, color: 'var(--text)' },
  sub: { fontSize: 12, color: 'var(--muted)', marginTop: 2 },
  primary: { fontSize: 13, padding: '9px 16px', borderRadius: 9, border: 'none', background: 'var(--gold2)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' },
  btn: { fontSize: 13, padding: '8px 14px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit' },
  btnDel: { fontSize: 13, padding: '8px 14px', borderRadius: 8, border: '0.5px solid rgba(224,112,112,.4)', background: 'rgba(224,112,112,.08)', color: '#b4313c', cursor: 'pointer', fontFamily: 'inherit' },
  empty: { color: 'var(--muted)', fontSize: 14, padding: '40px 20px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12 },
  planCard: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 12, border: '0.5px solid var(--border)', background: 'var(--hull)', marginBottom: 10 },
  planTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text)' },
  planMeta: { fontSize: 12, color: 'var(--muted)', marginTop: 3 },
  section: { border: '0.5px solid var(--border)', borderRadius: 14, padding: '16px 18px', marginBottom: 14, background: 'var(--hull)' },
  sectionAccent: { border: '0.5px solid var(--gold2)', background: 'linear-gradient(135deg, rgba(201,147,58,.06), rgba(201,147,58,.02))' },
  secTitle: { fontSize: 13, fontWeight: 700, color: 'var(--gold2)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 14 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  fieldLabel: { fontSize: 11.5, color: 'var(--muted)', marginBottom: 5, fontWeight: 600 },
  hint: { fontWeight: 400, fontStyle: 'italic', opacity: .8 },
  in: { width: '100%', padding: '8px 11px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--deep)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  ta: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--deep)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 },
  chip: { fontSize: 12, padding: '5px 11px', borderRadius: 16, border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' },
  chipOn: { background: 'rgba(201,147,58,.16)', border: '0.5px solid var(--gold2)', color: 'var(--gold2)' },
  ynRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '0.5px solid rgba(255,255,255,.04)' },
  ynLabel: { fontSize: 13, color: 'var(--text)', flex: 1 },
  ynBtns: { display: 'flex', gap: 4, flexShrink: 0 },
  yn: { fontSize: 11.5, padding: '4px 10px', borderRadius: 6, border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' },
  ynYes: { background: 'rgba(61,158,114,.16)', border: '0.5px solid #137050', color: '#137050' },
  ynNo: { background: 'rgba(224,112,112,.14)', border: '0.5px solid #b4313c', color: '#b4313c' },
  ynNa: { background: 'rgba(255,255,255,.06)', border: '0.5px solid var(--border)', color: 'var(--muted)' },
  autoNote: { fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', marginBottom: 12 },
  autoCard: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', borderRadius: 10, border: '0.5px solid var(--border)', background: 'var(--deep)', marginBottom: 8 },
  regsCard: { padding: '13px 15px', borderRadius: 10, border: '0.5px solid var(--border)', background: 'var(--deep)', marginBottom: 8 },
  regsHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 },
  regsTitle: { fontWeight: 700, fontSize: 13, marginBottom: 2 },
  regsBody: { marginTop: 8 },
  regsAnswer: { fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', color: 'var(--text)' },
  regsChips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  regsChip: { fontSize: 10.5, padding: '3px 8px', borderRadius: 999, background: 'rgba(106,170,212,.12)', border: '0.5px solid rgba(106,170,212,.3)', color: '#2a6a96', whiteSpace: 'nowrap' },
  regsDisc: { marginTop: 10, fontSize: 10.5, color: 'var(--muted)', fontStyle: 'italic' },
  regsErr: { fontSize: 12, color: '#b4313c', marginTop: 4 },
  autoLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  autoSub: { fontSize: 11.5, color: 'var(--muted)', marginTop: 2 },
  autoBadge: { fontSize: 10, padding: '3px 8px', borderRadius: 5, background: 'rgba(201,147,58,.12)', border: '0.5px solid var(--gold2)', color: 'var(--gold2)', whiteSpace: 'nowrap' },
  signBtn: { fontSize: 13, padding: '9px 16px', borderRadius: 9, border: 'none', background: '#137050', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' },
  sigGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginTop: 8 },
  sigCell: { border: '0.5px solid var(--border)', borderRadius: 10, padding: 10, background: 'var(--deep)' },
  sigRole: { fontSize: 12.5, fontWeight: 600, color: 'var(--text)', marginBottom: 6 },
  sigPad: { width: '100%', height: 90, border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--ink, #0a1119)', cursor: 'crosshair', display: 'block', touchAction: 'none' },
  sigClear: { position: 'absolute', top: 4, right: 4, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--muted)', fontFamily: 'inherit' },
  sigDate: { fontSize: 11, color: 'var(--muted)', marginTop: 6 },
  liveCard: { border: '0.5px solid var(--border)', borderRadius: 10, padding: '12px 13px', background: 'var(--deep)', marginBottom: 8 },
  liveHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 },
  liveTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  liveBtn: { fontSize: 12, padding: '5px 12px', borderRadius: 7, border: '0.5px solid var(--gold2)', background: 'rgba(201,147,58,.10)', color: 'var(--gold2)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  liveErr: { fontSize: 12, color: '#b4313c', margin: '4px 0' },
  spdGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, alignItems: 'end' },
  spdStat: { padding: '6px 0' },
  spdVal: { fontSize: 20, fontWeight: 700, color: 'var(--gold2)' },
  spdLbl: { fontSize: 10.5, color: 'var(--muted)', marginTop: 2 },
  legRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  legChip: { fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '0.5px solid var(--border)', color: 'var(--muted)' },
  tidePort: { marginTop: 8, paddingTop: 8, borderTop: '0.5px solid rgba(255,255,255,.05)' },
  tidePortName: { fontSize: 12.5, fontWeight: 600, color: 'var(--gold2)', marginBottom: 6 },
  tideRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  tideChip: { fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '0.5px solid var(--border)' },
  tideHigh: { background: 'rgba(106,170,212,.14)', border: '0.5px solid #2a6a96', color: '#2a6a96' },
  tideLow: { background: 'rgba(201,147,58,.12)', border: '0.5px solid var(--gold2)', color: 'var(--gold2)' },
  ukcGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 },
  ukcResult: { marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 14, fontWeight: 600, display: 'inline-block' },
  ukcGood: { background: 'rgba(61,158,114,.16)', border: '0.5px solid #137050', color: '#137050' },
  ukcWarn: { background: 'rgba(201,147,58,.16)', border: '0.5px solid var(--gold2)', color: 'var(--gold2)' },
  ukcBad: { background: 'rgba(224,112,112,.16)', border: '0.5px solid #b4313c', color: '#b4313c' },
  wxHeadline: { fontSize: 13, color: 'var(--text)', padding: '8px 10px', borderRadius: 8, background: 'rgba(0,0,0,.18)', border: '0.5px solid var(--border)', marginBottom: 4 },
  wxPtHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  wxCoord: { fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' },
  wxEta: { fontSize: 11.5, color: '#2a6a96', marginTop: 2, marginBottom: 4 },
  wxLineLabel: { fontSize: 10.5, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', margin: '6px 0 4px' },
  tlWrap: { marginTop: 10, padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--border)', background: 'var(--deep)' },
  tlHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  tlPlay: { width: 30, height: 30, borderRadius: 15, border: '0.5px solid var(--gold2)', background: 'rgba(201,147,58,.12)', color: 'var(--gold2)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, flexShrink: 0 },
  tlTime: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  tlHint: { fontSize: 11, color: 'var(--muted)', marginLeft: 'auto', fontStyle: 'italic' },
  tlSlider: { width: '100%', accentColor: '#8a5c0a', cursor: 'pointer' },
  tlLegend: { display: 'flex', gap: 14, marginTop: 6, fontSize: 11, color: 'var(--muted)', flexWrap: 'wrap' },
  tlDot: { display: 'inline-block', width: 10, height: 10, borderRadius: 5, marginRight: 4, verticalAlign: 'middle' },
  tlReadout: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 },
  tlRow: { display: 'grid', gridTemplateColumns: '1.4fr 1fr auto auto auto', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '0.5px solid rgba(255,255,255,.05)', fontSize: 11.5 },
  tlRowName: { fontWeight: 600, color: 'var(--text)' },
  tlRowWind: { fontWeight: 600 },
  tlRowSea: { color: 'var(--muted)', whiteSpace: 'nowrap' },
  tlRowSwell: { color: '#2a6a96', fontWeight: 600, whiteSpace: 'nowrap' },
  tlHereRow: { display: 'grid', gridTemplateColumns: '1.4fr 1fr auto auto auto', gap: 8, alignItems: 'center', padding: '5px 0', marginBottom: 3, borderRadius: 4, background: 'rgba(106,170,212,.10)', border: '0.5px solid rgba(106,170,212,.35)', fontSize: 11.5 },
  tlHereName: { color: '#2a6a96', fontWeight: 700, paddingLeft: 4 },
  wxNow: { display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 8px' },
  wxBig: { fontSize: 26, fontWeight: 600, color: 'var(--text)', lineHeight: 1 },
  wxCond: { fontSize: 12.5, color: 'var(--muted)' },
  wxChip: { fontSize: 11, padding: '3px 8px', borderRadius: 6 },
  wxChipPlain: { fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '0.5px solid var(--border)', color: 'var(--muted)' },
  wxDays: { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 },
  wxDayRow: { display: 'grid', gridTemplateColumns: '90px 1fr auto auto auto', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '0.5px solid rgba(255,255,255,.04)' },
  wxDayDate: { fontSize: 11.5, fontWeight: 600, color: 'var(--text)' },
  wxDayCell: { fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap' },
}


// ---- Fuel plan: 3 speed options, fuel on board, passage fuel, endurance, actual used ----
function FuelPlanSection({ form, setForm, distNm, plannedSpeed }) {
  const fd = form.auto_data?.fuel || {}
  const blank = { speed: '', rpm: '', lph: '' }
  const opts = (fd.options && fd.options.length === 3) ? fd.options : [blank, blank, blank]
  const unit = fd.unit || 'L'
  const fob = parseFloat(fd.on_board) || 0
  const reservePct = fd.reserve_pct != null && fd.reserve_pct !== '' ? parseFloat(fd.reserve_pct) : 10
  const arrActual = parseFloat(fd.on_arrival_actual)
  const num = v => { const n = parseFloat(v); return isFinite(n) ? n : null }
  const fmt = (v, d = 0) => v == null || !isFinite(v) ? '\u2013' : v.toLocaleString('en-GB', { maximumFractionDigits: d, minimumFractionDigits: d })
  const hm = h => { if (h == null || !isFinite(h)) return '\u2013'; const m = Math.round(h * 60); return Math.floor(m / 60) + 'h ' + String(m % 60).padStart(2, '0') + 'm' }
  const save = patch => setForm(f => ({ ...f, auto_data: { ...(f.auto_data || {}), fuel: { ...((f.auto_data || {}).fuel || {}), ...patch } } }))
  const setOpt = (i, k, v) => { const next = opts.map((o, j) => j === i ? { ...o, [k]: v } : o); save({ options: next }) }
  const useOpt = i => { save({ selected: i }); const sp = num(opts[i].speed); if (sp) setForm(f => ({ ...f, auto_data: { ...(f.auto_data || {}), speed_kn: String(sp) } })) }
  const rows = opts.map((o, i) => {
    const sp = num(o.speed), lph = num(o.lph)
    const hrs = sp && distNm ? distNm / sp : null
    const used = hrs != null && lph ? hrs * lph : null
    const left = used != null && fob ? fob - used : null
    const endur = lph && fob ? fob / lph : null
    const range = endur != null && sp ? endur * sp : null
    const perNm = lph && sp ? lph / sp : null
    const low = left != null && fob ? left < fob * reservePct / 100 : false
    return { i, sp, lph, hrs, used, left, endur, range, perNm, low }
  })
  const sel = fd.selected != null ? fd.selected : rows.findIndex(r => r.sp && plannedSpeed && Math.abs(r.sp - plannedSpeed) < 0.05)
  const planned = rows[sel] || null
  const actualUsed = fob && isFinite(arrActual) ? fob - arrActual : null
  const S = {
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 14 },
    h: { fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 10 },
    lbl: { fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 },
    in: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', color: '#16233B', fontSize: 13 },
    th: { fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--muted)', padding: '7px 8px', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
    td: { fontSize: 13, padding: '7px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text)', whiteSpace: 'nowrap' },
    cellIn: { width: 72, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', color: '#16233B', fontSize: 13 },
  }
  return (
    <div style={S.card}>
      <div style={S.h}>Fuel plan</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div><div style={S.lbl}>Fuel on board</div><input style={S.in} type="number" value={fd.on_board ?? ''} placeholder="e.g. 18000" onChange={e => save({ on_board: e.target.value })} /></div>
        <div><div style={S.lbl}>Unit</div><select style={S.in} value={unit} onChange={e => save({ unit: e.target.value })}><option value="L">Litres</option><option value="gal">US gallons</option></select></div>
        <div><div style={S.lbl}>Reserve (%)</div><input style={S.in} type="number" value={fd.reserve_pct ?? 10} onChange={e => save({ reserve_pct: e.target.value })} /></div>
        <div><div style={S.lbl}>Passage distance</div><div style={{ fontSize: 15, fontWeight: 700, paddingTop: 6 }}>{fmt(distNm, 1)} NM</div></div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={S.th}></th><th style={S.th}>Speed (kn)</th><th style={S.th}>RPM</th><th style={S.th}>Consumption ({unit}/h)</th>
            <th style={S.th}>Passage time</th><th style={S.th}>Fuel for passage</th><th style={S.th}>Left on arrival</th>
            <th style={S.th}>Endurance</th><th style={S.th}>Range</th><th style={S.th}>{unit}/NM</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.i} style={{ background: r.i === sel ? 'rgba(176,138,46,.10)' : 'transparent' }}>
                <td style={{ ...S.td, fontWeight: 700 }}>{['Economy', 'Cruise', 'Fast'][r.i]}</td>
                <td style={S.td}><input style={S.cellIn} type="number" value={opts[r.i].speed} onChange={e => setOpt(r.i, 'speed', e.target.value)} /></td>
                <td style={S.td}><input style={S.cellIn} type="number" value={opts[r.i].rpm} onChange={e => setOpt(r.i, 'rpm', e.target.value)} /></td>
                <td style={S.td}><input style={S.cellIn} type="number" value={opts[r.i].lph} onChange={e => setOpt(r.i, 'lph', e.target.value)} /></td>
                <td style={S.td}>{hm(r.hrs)}</td>
                <td style={S.td}>{r.used != null ? fmt(r.used) + ' ' + unit : '\u2013'}</td>
                <td style={{ ...S.td, color: r.low ? '#C93B47' : 'inherit', fontWeight: r.low ? 700 : 400 }}>{r.left != null ? fmt(r.left) + ' ' + unit + (r.low ? ' (below reserve)' : '') : '\u2013'}</td>
                <td style={S.td}>{hm(r.endur)}</td>
                <td style={S.td}>{r.range != null ? fmt(r.range) + ' NM' : '\u2013'}</td>
                <td style={S.td}>{fmt(r.perNm, 1)}</td>
                <td style={S.td}><button onClick={() => useOpt(r.i)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: r.i === sel ? 'var(--gold)' : 'transparent', color: r.i === sel ? '#000' : 'var(--text)', fontSize: 12, cursor: 'pointer' }}>{r.i === sel ? 'Planned' : 'Use'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginTop: 14 }}>
        <div><div style={S.lbl}>Fuel on arrival (actual)</div><input style={S.in} type="number" value={fd.on_arrival_actual ?? ''} placeholder="after the passage" onChange={e => save({ on_arrival_actual: e.target.value })} /></div>
        <div><div style={S.lbl}>Actually used</div><div style={{ fontSize: 15, fontWeight: 700, paddingTop: 6 }}>{actualUsed != null ? fmt(actualUsed) + ' ' + unit : '\u2013'}</div></div>
        <div><div style={S.lbl}>Planned ({planned ? ['Economy', 'Cruise', 'Fast'][planned.i] : 'none'})</div><div style={{ fontSize: 15, fontWeight: 700, paddingTop: 6 }}>{planned && planned.used != null ? fmt(planned.used) + ' ' + unit : '\u2013'}</div></div>
        <div><div style={S.lbl}>Difference</div><div style={{ fontSize: 15, fontWeight: 700, paddingTop: 6, color: actualUsed != null && planned?.used != null && actualUsed > planned.used ? '#C93B47' : '#137050' }}>{actualUsed != null && planned?.used != null ? (actualUsed - planned.used > 0 ? '+' : '') + fmt(actualUsed - planned.used) + ' ' + unit : '\u2013'}</div></div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 10 }}>Enter speed, RPM and consumption for three settings (from the engine manufacturer's curve or your own logs). "Use" sets that speed as the planned passage speed; ETAs, arrival and weather follow it.</div>
    </div>
  )
}

// ---- Pre-departure: what must be done before letting go (auto status where the app knows) ----
function PreDepartureSection({ form, setForm, clientId }) {
  const pd = form.auto_data?.predep || {}
  const ticks = pd.ticks || {}
  const custom = pd.custom || []
  const [runs, setRuns] = useState({})
  const [newItem, setNewItem] = useState('')
  const save = patch => setForm(f => ({ ...f, auto_data: { ...(f.auto_data || {}), predep: { ...((f.auto_data || {}).predep || {}), ...patch } } }))
  const who = () => 'Bridge'
  const toggle = key => {
    const cur = ticks[key]
    save({ ticks: { ...ticks, [key]: cur?.done ? { done: false } : { done: true, at: new Date().toISOString(), by: who() } } })
  }
  // latest completed runs of the pre-departure checklists (deck PD-01, engine room PD-02)
  useEffect(() => {
    if (!clientId) return
    let live = true
    ;(async () => {
      try {
        const { data: cl } = await supabase.from('checklists').select('id,title').eq('client_id', clientId)
        const find = re => (cl || []).filter(c => re.test(c.title || '')).map(c => c.id)
        const groups = { deck: find(/pre-?departure.*deck|^PD-01/i), engine: find(/pre-?departure.*engine|^PD-02/i) }
        const out = { titles: {} }
        for (const [k, ids] of Object.entries(groups)) {
          if (!ids.length) continue
          out.titles[k] = ((cl || []).find(c => c.id === ids[0]) || {}).title
          const { data: inst } = await supabase.from('checklist_instances').select('id,completed_at').in('checklist_id', ids).not('completed_at', 'is', null).order('completed_at', { ascending: false }).limit(1)
          if (inst && inst[0]) out[k] = inst[0].completed_at
        }
        if (live) setRuns(out)
      } catch (e) {}
    })()
    return () => { live = false }
  }, [clientId])
  const depMs = form.departure_date ? new Date(form.departure_date + 'T' + toHHMM(form.departure_time_local)).getTime() : NaN
  const recent = iso => { if (!iso) return false; const t = new Date(iso).getTime(); return isNaN(depMs) ? (Date.now() - t < 48 * 3600e3) : (t <= depMs + 3600e3 && depMs - t < 48 * 3600e3) }
  const fmtT = iso => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  const auto = {
    deck: runs.deck && recent(runs.deck) ? 'Completed ' + fmtT(runs.deck) : null,
    engine: runs.engine && recent(runs.engine) ? 'Completed ' + fmtT(runs.engine) : null,
    weather: form.auto_data?.weather ? 'Weather along the route fetched' : null,
    signed: (form.signatures && Object.keys(form.signatures).length) || form.status === 'signed' ? 'Passage plan signed' : null,
  }
  const ITEMS = [
    { key: 'crew_list', label: 'Crew list prepared for port clearance / agent', hint: 'Crew \u2192 Crew List', go: () => hcGo('crew', () => localStorage.setItem('hc_crew_tab', 'crew_list')), goLabel: 'Open crew list' },
    { key: 'deck', label: 'Deck pre-departure checklist (PD-01)', hint: 'Checklists \u2192 PD-01', auto: auto.deck, go: () => hcGo('checklists', () => runs.titles?.deck && localStorage.setItem('hc_open_checklist', runs.titles.deck)), goLabel: 'Start checklist' },
    { key: 'engine', label: 'Engine room pre-departure checklist (PD-02)', hint: 'Checklists \u2192 PD-02', auto: auto.engine, go: () => hcGo('checklists', () => runs.titles?.engine && localStorage.setItem('hc_open_checklist', runs.titles.engine)), goLabel: 'Start checklist' },
    { key: 'weather', label: 'Weather checked for route and destination', auto: auto.weather },
    { key: 'briefing', label: 'Crew and guests briefed on passage and weather' },
    { key: 'port', label: 'Port / marina / agent informed of departure time' },
    { key: 'signed', label: 'Passage plan approved and signed by the Master', auto: auto.signed },
    ...custom.map((c, i) => ({ key: 'c' + i, label: c, customIdx: i })),
  ]
  const done = ITEMS.filter(it => it.auto || ticks[it.key]?.done).length
  const S = {
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 14 },
    row: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' },
    box: on => ({ width: 20, height: 20, borderRadius: 5, border: '1.5px solid ' + (on ? '#137050' : 'var(--border)'), background: on ? '#137050' : '#fff', color: '#fff', fontSize: 13, lineHeight: '18px', textAlign: 'center', cursor: 'pointer', flex: '0 0 20px' }),
  }
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Before departure</div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: done === ITEMS.length ? '#137050' : '#76601f' }}>{done} / {ITEMS.length} done</div>
      </div>
      {ITEMS.map(it => {
        const on = !!(it.auto || ticks[it.key]?.done)
        return (
          <div key={it.key} style={S.row}>
            <div style={S.box(on)} onClick={() => !it.auto && toggle(it.key)} title={it.auto ? 'Detected automatically' : 'Tick when done'}>{on ? '\u2713' : ''}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, color: 'var(--text)', textDecoration: on ? 'none' : 'none' }}>{it.label}</div>
              <div style={{ fontSize: 11.5, color: it.auto ? '#137050' : 'var(--muted)' }}>
                {it.auto ? '\u2713 ' + it.auto : ticks[it.key]?.done ? 'Ticked ' + fmtT(ticks[it.key].at) : (it.hint || '')}
              </div>
            </div>
            {it.go && !it.auto && <button className="no-print" onClick={it.go} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gold)', background: 'transparent', color: 'var(--text)', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>{it.goLabel + ' \u2192'}</button>}
            {it.customIdx != null && <button onClick={() => save({ custom: custom.filter((_, j) => j !== it.customIdx) })} style={{ border: 'none', background: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}>{'\u2715'}</button>}
          </div>
        )
      })}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Add a task for this passage (e.g. bunkering, tender stowed for sea)" style={{ flex: 1, padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', color: '#16233B', fontSize: 13 }} />
        <button onClick={() => { const t = newItem.trim(); if (!t) return; save({ custom: [...custom, t] }); setNewItem('') }} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--gold)', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Add</button>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>Deck and engine checklists, weather and signatures tick themselves when they are done in Hey Captain; tick the rest by hand. Saved with the plan.</div>
    </div>
  )
}

const SO_DEFAULTS = [
  { key: 'standing_orders', title: 'Bridge Watch Standing Orders', body: `Standing Orders are to be followed unless specifically informed otherwise by the Captain.

- Perform your pre-watch and watch duties as described in the Watch Guidelines and these Standing Orders.
- The Captain will inform the Watch Officer when he has the Conn.
- Maintain watch on navigational warnings and weather.
- When the Captain has the Conn, stand clear and continue your duties.
- When in doubt, call the Captain.
- Inform the Captain of a 2.0-mile CPA.
- Immediately inform the Captain of all traffic which will come within 1 nm.
- Inform the Captain of Nav/Com irregularities.
- Inform the Engineer of instrumentation irregularities.
- Perform a complete instrument check every 60 minutes.
- Regular ranging of radar to check for long-range targets.
- Perform traffic checks continuously.
- Take caution for night vision. Do not use bright lights.
- Do not adjust the instruments without prior consent from the Captain.
- Running lights are to be turned on 30 minutes prior to sunset and during limited visibility (<2 nm).
- Do not adjust throttle settings without consent from the Captain (except in an emergency or if otherwise instructed by the Captain).

If you are unsure of the above procedure, ask for clarification.` },
  { key: 'watch_leader', title: 'Bridge Watch Guidelines \u2013 Watch Leader', body: `The following points are guidelines to help the Watch Leader understand, in general terms, what is expected regarding watch responsibilities. The Standing Orders are posted on the Bridge and give specific guidance as to how to conduct your watch.

- The Watch Leader is to strictly adhere to the Bridge Standing Orders, unless otherwise instructed by the Captain.
- The Watch Leader is to be on deck 15 minutes prior to his watch.
- The on-coming Watch Leader is to review the chart, the running log, and the current weather conditions (in that order).
- During the 15-minute acclimation time, the off-going OOW will inform the on-coming OOW of:
  1. Instructions from the Captain
  2. Weather status
  3. Ship handling characteristics
  4. Traffic status \u2014 past, present, and future
  5. Radio contacts
  6. Major events (i.e., course changes, sightings, shipboard or other events)
  7. Instrumentation status
  8. Nav/Com equipment status
- The Watch Officer:
  1. May sit during his watch
  2. Is to make a visual E/R check every non-watch-changing hour (traffic/weather permitting)
  3. Is to ensure all log entries made during his watch are completed, accurate, and neat
  4. Is to make an hourly handwritten log entry

Note: The responsibility of the Watch Leader is to assure that a thorough forward-looking watch is being maintained at all times. It is imperative to the safety of the ship that the Watch Leader does not permit the lookout to be distracted by anyone or anything during his/her watch.

Please conduct yourself in a professional manner at all times. Do not go on deck without prior notification to, and agreement from, the Captain.` },
  { key: 'lookout', title: 'Bridge Watch Guidelines \u2013 Lookout', body: `The following points are guidelines to help the Lookout understand, in general terms, what is expected regarding watch responsibilities. The Standing Orders are posted on the Bridge and provide specific guidance on how to conduct your watch.

- The Lookout is to strictly adhere to the Bridge Standing Orders.
- The Lookout is to be on deck 15 minutes prior to his watch, showered, in boat clothing, with a charged radio and any glasses needed.
- The on-coming Lookout is to review the chart, the running log, and the current weather conditions (in that order).
- During the 15-minute acclimation period, the off-going watch will inform the on-coming watch of:
  1. Weather status
  2. Ship handling characteristics
  3. Traffic status \u2014 past, present, and future
  4. Radio contacts
  5. Major events (e.g., course changes, sightings, shipboard or other events)
  6. Instrumentation status
  7. Nav/Com equipment status
- The Lookout is to continue the 15-minute cycle according to the Standing Orders and checklist.
- The off-going Lookout is to perform an engine room check as directed by the engineer:
  1. Check according to the engineer\u2019s instructions.
  2. Do not touch or manipulate any switches, valves, or levers.
  3. The check is strictly audio-visual.

Note: The responsibility of the Lookout is, quite simply, to look out. It is imperative for the safety of the ship that the Lookout does not allow himself to become distracted by anyone or anything during his watch. Keep your eyes forward at all times.

Please do not go on deck without prior notification to, and agreement from, the Captain. Act in a deliberate and carefully thought-out manner.` },
]

// ---- navigation to another page of the app (App listens for this event) ----
function hcGo(page, prep) {
  try { if (prep) prep() } catch (e) {}
  try { localStorage.setItem('hc_page', page) } catch (e) {}
  window.__hcNavHandled = false
  window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page } }))
  // nobody listening (older app shell): reload - the app opens the remembered page
  setTimeout(() => { if (!window.__hcNavHandled) window.location.reload() }, 150)
}

// fraction (0..1) of the route distance at the point on the route line closest to lat/lon
function alongTrackFrac(wps, lat, lon) {
  if (!wps || wps.length < 2 || lat == null || lon == null) return null
  const rd = routeDistances(wps); if (!rd.total) return null
  const k = Math.cos(lat * Math.PI / 180)
  let best = null, bd = Infinity
  for (let i = 1; i < wps.length; i++) {
    const ax = wps[i - 1].lon * k, ay = wps[i - 1].lat, bx = wps[i].lon * k, by = wps[i].lat, px = lon * k, py = lat
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy
    let t = L2 ? ((px - ax) * dx + (py - ay) * dy) / L2 : 0; t = Math.max(0, Math.min(1, t))
    const d = Math.hypot(ax + t * dx - px, ay + t * dy - py)
    if (d < bd) { bd = d; best = (rd.cum[i - 1] + t * (rd.legs[i - 1] || 0)) / rd.total }
  }
  return best
}
function escHtml(t) { return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;') }   // quotes too: also used in attributes (zap-active)
// plain text with "- " bullets and "  1." sub-items -> HTML (used for standing orders in the PDF)
function soToHtml(text) {
  const lines = String(text || '').split('\n')
  let html = '', inUl = false, inOl = false
  const close = () => { if (inOl) { html += '</ol>'; inOl = false } if (inUl) { html += '</li></ul>'; inUl = false } }
  for (const raw of lines) {
    const t = raw.trim()
    if (/^\d+\.\s+/.test(t) && inUl) {
      if (!inOl) { html += '<ol style="margin:3px 0 3px 18px;padding:0;">'; inOl = true }
      html += '<li style="margin:1px 0;">' + escHtml(t.replace(/^\d+\.\s+/, '')) + '</li>'; continue
    }
    if (inOl) { html += '</ol>'; inOl = false }
    if (/^[-\u2022]\s+/.test(t)) {
      if (!inUl) { html += '<ul style="margin:4px 0 4px 16px;padding:0;"><li style="margin:2px 0;">'; inUl = true } else html += '</li><li style="margin:2px 0;">'
      html += escHtml(t.replace(/^[-\u2022]\s+/, '')); continue
    }
    close()
    if (!t) { html += '<div style="height:5px"></div>'; continue }
    html += '<div style="margin:3px 0;">' + escHtml(t).replace(/^(Note:)/, '<b>$1</b>') + '</div>'
  }
  close()
  return html
}
// markdown (### headings, - bullets, **bold**) -> HTML (applicable regulations in the PDF)
function mdToHtml(text) {
  const inl = s => escHtml(s).replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
  let html = '', inUl = false
  for (const raw of String(text || '').split('\n')) {
    const t = raw.trim()
    if (/^[-*\u2022]\s+/.test(t)) { if (!inUl) { html += '<ul style="margin:3px 0 3px 16px;padding:0;">'; inUl = true } html += '<li style="margin:2px 0;">' + inl(t.replace(/^[-*\u2022]\s+/, '')) + '</li>'; continue }
    if (inUl) { html += '</ul>'; inUl = false }
    if (!t) continue
    if (/^#{1,4}\s+/.test(t)) { html += '<div style="font-weight:700;margin:8px 0 2px;">' + inl(t.replace(/^#{1,4}\s+/, '').replace(/\*\*/g, '')) + '</div>'; continue }
    html += '<div style="margin:3px 0;">' + inl(t) + '</div>'
  }
  if (inUl) html += '</ul>'
  return html
}

// ---- Standing orders & watch guidelines: per-vessel documents, a copy saved with every plan ----
function StandingOrdersSection({ form, setForm, clientId }) {
  const so = form.auto_data?.standing_orders
  const [tab, setTab] = useState(0)
  const [msg, setMsg] = useState('')
  const [editing, setEditing] = useState(false)
  const docs = (so && so.docs && so.docs.length) ? so.docs : SO_DEFAULTS
  const ack = (so && so.ack) || {}
  const save = patch => setForm(f => ({ ...f, auto_data: { ...(f.auto_data || {}), standing_orders: { ...(((f.auto_data || {}).standing_orders) || { docs: SO_DEFAULTS, ack: {} }), ...patch } } }))
  // first open of a plan: take the vessel's current standing orders (or the defaults)
  useEffect(() => {
    if (so && so.docs && so.docs.length) return
    let live = true
    ;(async () => {
      let vesselDocs = null
      try {
        if (clientId) {
          const { data } = await supabase.from('vessel_documents').select('doc_key,title,body').eq('client_id', clientId).in('doc_key', SO_DEFAULTS.map(d => d.key))
          if (data && data.length) vesselDocs = SO_DEFAULTS.map(d => { const v = data.find(x => x.doc_key === d.key); return v ? { key: d.key, title: v.title || d.title, body: v.body || d.body } : d })
        }
      } catch (e) {}
      if (live) save({ docs: vesselDocs || SO_DEFAULTS, ack: ack })
    })()
    return () => { live = false }
  }, [clientId]) // eslint-disable-line
  const setDoc = (i, k, v) => save({ docs: docs.map((d, j) => j === i ? { ...d, [k]: v } : d) })
  async function saveForVessel() {
    if (!clientId) return
    setMsg('Saving\u2026')
    const rows = docs.map(d => ({ client_id: clientId, doc_key: d.key, title: d.title, body: d.body, updated_at: new Date().toISOString() }))
    const { error } = await supabase.from('vessel_documents').upsert(rows, { onConflict: 'client_id,doc_key' })
    setMsg(error ? 'Could not save: ' + error.message : '\u2713 Saved as the vessel\u2019s standing orders \u2014 new passage plans will use this version.')
  }
  const people = [
    ...((form.watch_leaders || []).map(n => ({ name: n, role: 'Watch leader' }))),
    ...(((form.auto_data?.watch_bill?.lookouts) || []).map(n => ({ name: n, role: 'Lookout' }))),
  ].filter((p, i, a) => a.findIndex(q => q.name === p.name) === i)
  const toggleAck = name => save({ ack: { ...ack, [name]: ack[name] ? null : new Date().toISOString() } })
  const S = {
    tab: on => ({ padding: '6px 12px', borderRadius: 7, border: '1px solid ' + (on ? 'var(--gold)' : 'var(--border)'), background: on ? 'rgba(176,138,46,.12)' : 'transparent', color: 'var(--text)', fontSize: 12.5, fontWeight: on ? 700 : 500, cursor: 'pointer' }),
    box: on => ({ width: 18, height: 18, borderRadius: 4, border: '1.5px solid ' + (on ? '#137050' : 'var(--border)'), background: on ? '#137050' : '#fff', color: '#fff', fontSize: 12, lineHeight: '16px', textAlign: 'center', cursor: 'pointer', flex: '0 0 18px' }),
  }
  const d = docs[tab] || docs[0]
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {docs.map((x, i) => <button key={x.key} style={S.tab(i === tab)} onClick={() => setTab(i)}>{x.title}</button>)}
        <div style={{ flex: 1 }} />
        <button className="no-print" style={S.tab(editing)} onClick={() => setEditing(e => !e)}>{editing ? 'Done editing' : 'Edit'}</button>
      </div>
      {editing ? (
        <div>
          <input value={d.title} onChange={e => setDoc(tab, 'title', e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', color: '#16233B', fontSize: 13, fontWeight: 700, marginBottom: 6 }} />
          <textarea value={d.body} onChange={e => setDoc(tab, 'body', e.target.value)} rows={16} style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', color: '#16233B', fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit' }} />
          <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '4px 0 8px' }}>Start a line with "- " for a bullet and "  1." for a numbered sub-point. Changes apply to this plan; "Save for the vessel" makes them the standard for new plans.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={saveForVessel} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--gold)', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Save for the vessel</button>
            <button onClick={() => setDoc(tab, 'body', (SO_DEFAULTS.find(x => x.key === d.key) || d).body)} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13 }}>Reset to default</button>
            {msg && <span style={{ fontSize: 12, color: /Could not/.test(msg) ? '#C93B47' : '#137050' }}>{msg}</span>}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{d.title}</div>
          <div dangerouslySetInnerHTML={{ __html: soToHtml(d.body) }} />
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>Read and understood</div>
        {people.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Select watch leaders (Passage Setup) and lookouts (Watch Bill) to list them here.</div>}
        {people.map(p => (
          <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={S.box(!!ack[p.name])} onClick={() => toggleAck(p.name)}>{ack[p.name] ? '\u2713' : ''}</div>
            <div style={{ flex: 1, fontSize: 13 }}>{p.name} <span style={{ color: 'var(--muted)', fontSize: 12 }}>{'\u00b7 ' + p.role}</span></div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{ack[p.name] ? new Date(ack[p.name]).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'not yet'}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function WatchBillSection({ form, setForm, crew }) {
  const saved = form.auto_data?.watch_bill || null
  const leaders = form.watch_leaders || []
  const [lookouts, setLookouts] = useState(saved?.lookouts || [])
  const [startHour, setStartHour] = useState(saved?.startHour ?? 6)
  const [watchLen, setWatchLen] = useState(saved?.watchLen ?? 4)
  const [lookoutOffset, setLookoutOffset] = useState(saved?.lookoutOffset ?? 2)
  const [bill, setBill] = useState(null)
  const [verdict, setVerdict] = useState(null)

  const hhmm = t => {
    if (!t) return null
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i)
    if (!m) return null
    let h = parseInt(m[1], 10)
    if (m[3]) { const pm = /PM/i.test(m[3]); if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12 }
    return String(h).padStart(2, '0') + ':' + m[2]
  }
  const depTime = hhmm(form.departure_time_local) || '00:00'
  const arrTime = hhmm(form.arrival_time_local) || '23:00'
  const dep = form.departure_date ? form.departure_date + 'T' + depTime : null
  const arr = form.arrival_date ? form.arrival_date + 'T' + arrTime : null
  const timesAssumed = !hhmm(form.departure_time_local) || !hhmm(form.arrival_time_local)

  function toggleLookout(name) {
    setLookouts(l => l.includes(name) ? l.filter(n => n !== name) : [...l, name])
  }

  function exportWatchBill() {
    if (!bill || !bill.rows.length) { alert('Generate the watch bill first.'); return }
    const NAVY = '#0A1628', GOLD = '#C9A84C', LINE = '#E5E9F1', MUTE = '#67748C', TEXT = '#16233B', BLUE = '#1155F5'
    const vessel = form.auto_data?.vessel_name || 'Vessel'
    const wave = '<span style="display:inline-block;font-size:28px;font-weight:900;color:' + BLUE + ';line-height:1;letter-spacing:-3px;">\u2248</span>'
    const fmt = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

    const dayTables = bill.days.map(d => {
      const hdr = Array.from({ length: 24 }, (_, h) => '<th style="padding:4px 1px;font-size:7.5px;color:#fff;background:' + NAVY + ';">' + String(h).padStart(2, '0') + '</th>').join('')
      const body = bill.rows.map(r => {
        const tag = r.kind === 'leader' ? 'WL' : r.kind === 'lookout' ? 'LO' : ''
        const cells = r.cells[d].split('').map(v => {
          const bg = v === 'W'
            ? (r.kind === 'leader' ? 'rgba(17,85,245,.65)' : r.kind === 'lookout' ? 'rgba(23,138,92,.45)' : 'rgba(201,168,76,.35)')
            : 'rgba(201,59,71,.06)'
          return '<td style="padding:0;height:15px;border:1px solid #fff;background:' + bg + ';"></td>'
        }).join('')
        return '<tr><td style="padding:3px 7px;font-size:8.5px;font-weight:600;white-space:nowrap;">' + escHtml(r.name) +
          ' <span style="color:' + MUTE + ';font-weight:400;">' + tag + '</span></td>' + cells + '</tr>'
      }).join('')
      return '<div style="margin-bottom:11px;">' +
        '<div style="font-size:9px;font-weight:700;color:' + MUTE + ';letter-spacing:.08em;margin-bottom:4px;">' + d + '</div>' +
        '<table style="width:100%;border-collapse:collapse;">' +
        '<thead><tr><th style="padding:4px 7px;text-align:left;font-size:8px;color:#fff;background:' + NAVY + ';">Crew</th>' + hdr + '</tr></thead>' +
        '<tbody>' + body + '</tbody></table></div>'
    }).join('')

    const key = '<div style="display:flex;gap:16px;font-size:8.5px;color:' + MUTE + ';margin-bottom:12px;">' +
      '<span><span style="display:inline-block;width:12px;height:8px;background:rgba(17,85,245,.65);margin-right:4px;"></span>Watch leader</span>' +
      '<span><span style="display:inline-block;width:12px;height:8px;background:rgba(23,138,92,.45);margin-right:4px;"></span>Lookout</span>' +
      '<span><span style="display:inline-block;width:12px;height:8px;background:rgba(201,168,76,.35);margin-right:4px;"></span>Day work</span></div>'

    const verd = verdict
      ? (verdict.ok
        ? '<div style="font-size:10px;color:#137050;font-weight:700;margin-bottom:10px;">MLC compliant across the whole passage &middot; bridge covered every hour</div>'
        : '<div style="font-size:10px;color:#C93B47;font-weight:700;margin-bottom:10px;">NOT MLC compliant &mdash; ' + verdict.issues.filter(i => i.level === 'red').length + ' violation(s)</div>')
      : ''

    const html = '<div style="width:1080px;padding:8px 12px;font-family:Inter,Helvetica,Arial,sans-serif;color:' + TEXT + ';background:#fff;">' +
      '<div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid ' + GOLD + ';padding-bottom:8px;margin-bottom:10px;">' +
        '<div><div style="display:flex;align-items:center;gap:10px;">' + wave +
          '<span style="font-size:19px;font-weight:800;letter-spacing:.13em;color:' + NAVY + ';">HEYCAPTAIN</span></div>' +
          '<div style="font-size:8.5px;color:' + MUTE + ';letter-spacing:.2em;text-transform:uppercase;margin-top:3px;margin-left:38px;">Underway Watch Bill</div></div>' +
        '<div style="text-align:right;font-size:9px;color:' + MUTE + ';line-height:1.7;">' +
          '<div style="font-size:13px;font-weight:700;color:' + TEXT + ';">' + escHtml(form.departure_port || '') + ' \u2192 ' + escHtml(form.arrival_port || '') + '</div>' +
          '<div>' + fmt(bill.days[0]) + ' &ndash; ' + fmt(bill.days[bill.days.length - 1]) +
          ' &middot; ' + (Number(watchLen) || 4) + 'h watches, lookouts +' + (Number(lookoutOffset) || 0) + 'h</div></div>' +
      '</div>' + verd + key + dayTables +
      '<div style="margin-top:12px;padding-top:8px;border-top:1px solid ' + LINE + ';display:flex;justify-content:space-between;font-size:8px;color:' + MUTE + ';">' +
        '<span style="font-weight:800;letter-spacing:.12em;color:' + NAVY + ';">HEYCAPTAIN</span>' +
        '<span>Watch Bill &middot; generated ' + new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + '</span></div>' +
    '</div>'

    const el = document.createElement('div')
    el.innerHTML = html
    document.body.appendChild(el)
    const fname = ('Watch_Bill_' + (form.departure_port || 'passage') + '_' + bill.days[0]).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    html2pdf().set({ margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, windowWidth: 1080, width: 1080 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } })
      .from(el).save().then(() => document.body.removeChild(el))
  }

  function build() {
    if (!dep || !arr) { alert('Set the departure and arrival dates in Passage Setup.'); return }
    if (!leaders.length) { alert('Pick watch leaders in Passage Setup (section 1) first.'); return }
    if (!lookouts.length) { alert('Pick at least one lookout.'); return }
    const others = crew.filter(n => !leaders.includes(n) && !lookouts.includes(n)).map(n => ({ name: n }))
    const b = generateWatchBill({
      dep, arr,
      startHour: Number(startHour) || 0,
      watchLen: Number(watchLen) || 4,
      lookoutOffset: Number(lookoutOffset) || 0,
      leaders: leaders.map(n => ({ name: n })),
      lookouts: lookouts.map(n => ({ name: n })),
      others,
    })
    const v = verifyWatchBill(b)
    setBill(b); setVerdict(v)
    setForm(f => ({ ...f, auto_data: { ...(f.auto_data || {}), watch_bill: { leaders, lookouts, startHour: Number(startHour) || 0, watchLen: Number(watchLen) || 4, lookoutOffset: Number(lookoutOffset) || 0, days: b.days, rows: b.rows, ok: v.ok } } }))
  }

  const chip = (on) => ({
    padding: '5px 11px', borderRadius: 8, fontSize: 11.5, cursor: 'pointer',
    border: '1px solid ' + (on ? 'var(--gold, #C9A84C)' : 'var(--border)'),
    background: on ? 'rgba(201,168,76,.14)' : 'transparent',
    color: on ? 'var(--text)' : 'var(--muted)', fontWeight: on ? 600 : 400,
  })

  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10 }}>
        Watch leaders rotate 4-hour watches; lookouts the same, offset 2 hours, so the bridge never changes over all at once. Everyone else works days (08–17). Tap a name for each pool.
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>
            Watch leaders <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>&mdash; from Passage Setup</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {leaders.length
              ? leaders.map(n => <span key={n} style={{ ...chip(true), cursor: 'default' }}>{n}</span>)
              : <span style={{ fontSize: 11.5, color: '#C93B47' }}>Pick watch leaders in Passage Setup above.</span>}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Lookouts</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {crew.filter(n => !leaders.includes(n)).map(n => (
              <button key={n} onClick={() => toggleLookout(n)} style={chip(lookouts.includes(n))}>{n}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Watch length</div>
            <select value={watchLen} onChange={e => setWatchLen(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12 }}>
              {[2, 3, 4, 6, 8].map(h => <option key={h} value={h}>{h} hours</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Lookout offset</div>
            <select value={lookoutOffset} onChange={e => setLookoutOffset(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12 }}>
              {Array.from({ length: Number(watchLen) || 4 }, (_, h) => <option key={h} value={h}>{h === 0 ? 'none' : '+' + h + 'h'}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>First watch starts</div>
            <select value={startHour} onChange={e => setStartHour(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12 }}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </div>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button onClick={build} style={{ padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>Generate watch bill</button>
          {bill && <button onClick={exportWatchBill} style={{ marginLeft: 8, padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 12.5, cursor: 'pointer' }}>Export PDF</button>}
        </div>
      </div>

      {timesAssumed && bill && (
        <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 11.5,
          background: 'rgba(185,125,20,.08)', border: '1px solid rgba(185,125,20,.3)', color: '#8a5c0a' }}>
          No departure/arrival times set — the bill assumes the passage runs the whole of each day. Set the times in Passage Setup for an exact bill.
        </div>
      )}

      {verdict && (
        <div style={{ padding: '9px 13px', borderRadius: 9, marginBottom: 12, fontSize: 12.5, fontWeight: 600,
          background: verdict.ok ? 'rgba(23,138,92,.08)' : 'rgba(201,59,71,.09)',
          border: '1px solid ' + (verdict.ok ? 'rgba(23,138,92,.3)' : 'rgba(201,59,71,.35)'),
          color: verdict.ok ? '#137050' : '#C93B47' }}>
          {verdict.ok ? 'MLC compliant across the whole passage \u00b7 bridge covered every hour' : 'NOT compliant:'}
          {!verdict.ok && verdict.issues.filter(i => i.level === 'red').slice(0, 5).map((i, k) => (
            <div key={k} style={{ fontWeight: 400, marginTop: 3 }}>{i.who} · {i.day} · {i.msg}</div>
          ))}
        </div>
      )}

      {bill && bill.rows.length > 0 && bill.days.map(d => (
        <div key={d} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.08em', marginBottom: 5 }}>{d}</div>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid var(--border)' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
              <thead><tr>
                <th style={{ padding: '5px 9px', textAlign: 'left', fontSize: 9, color: '#fff', background: '#0A1628' }}>Crew</th>
                {Array.from({ length: 24 }, (_, h) => <th key={h} style={{ padding: '5px 2px', fontSize: 8.5, color: '#fff', background: '#0A1628', minWidth: 26 }}>{String(h).padStart(2, '0')}</th>)}
              </tr></thead>
              <tbody>
                {bill.rows.map(r => (
                  <tr key={r.name}>
                    <td style={{ padding: '4px 9px', fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap' }}>
                      {r.name} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{r.kind === 'leader' ? 'WL' : r.kind === 'lookout' ? 'LO' : ''}</span>
                    </td>
                    {r.cells[d].split('').map((v, h) => (
                      <td key={h} style={{ padding: 0, height: 20, minWidth: 26, border: '1px solid #fff',
                        background: v === 'W' ? (r.kind === 'leader' ? 'rgba(17,85,245,.65)' : r.kind === 'lookout' ? 'rgba(23,138,92,.45)' : 'rgba(201,168,76,.35)') : 'rgba(201,59,71,.06)' }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}
